/**
 * GameConfig — every tunable constant and balance function of the simulation.
 *
 * Organisation: one exported plain object per SECTION (TIMING, SPAWN, POPULATION, GOLD,
 * ATTACK, TERRAIN, ENCLAVE, NAVAL, TRADE, WARSHIP, BUILD, STRUCTURES, RAIL, NUKES, SAM,
 * FALLOUT, DIPLOMACY, SOCIAL, AI_DIFFICULTY). Gameplay engineers tune their own section.
 * A `GameConfig` instance (game.config) combines the sections with the game's settings
 * (gameSpeed, infiniteGold, infiniteTroops, instantBuild, difficulty, disabled units …) and
 * exposes the balance functions. Per-game overrides (tests, sandbox) can be passed via
 * `GameConfigOverrides` without mutating the module-level defaults.
 *
 * Determinism: only + - * / sqrt / floor etc. (see docs/TECHNICAL.md#determinism-rules). Use pow075().
 *
 * Pace target (world map ≈ 2000×1000, ~150 bots + nations): an active human grows from
 * spawn to ~1–2 % of the land in ~2 minutes; full games last 20–40 minutes.
 */
import { Difficulty, featureOn, PlayerType, TerrainType, UnitType, type GameSettings } from "./types";
import { clamp, pow075 } from "./util/math";
import { resolveSettings } from "./rules/ruleset";

// =============================================================================
// 1. TIMING
// =============================================================================

/** Milliseconds per simulation tick (10 ticks / second). */
export const TICK_MS = 100;
export const TICKS_PER_SECOND = 1000 / TICK_MS;
/** A deterministic state hash is attached to every TickUpdate with tick % HASH_INTERVAL === 0. */
export const HASH_INTERVAL = 50;
/** A StatsSample is attached to every TickUpdate with tick % STATS_SAMPLE_INTERVAL === 0. */
export const STATS_SAMPLE_INTERVAL = 50;

export function secondsToTicks(seconds: number): number {
  return Math.round(seconds * TICKS_PER_SECOND);
}

// =============================================================================
// 2. SPAWN
// =============================================================================

export const SPAWN = {
  /** Spawn reach: the random start shape stays within about this many tiles (+2) of the spawn tile. */
  radius: 4,
  /** Owner feedback: every player (human, nation, tribe) starts with this many tiles, in a random shape. */
  startTiles: 50,
  /** A spawn is rejected when another player's territory is within this distance. */
  minDistanceToOthers: 8,
  /** Starting population (troops + workers) of a human. */
  startPopulation: 5_000,
  /** Bots start with this fraction of startPopulation. */
  botStartPopulationMult: 0.6,
  /** Nations get (1 + bonus * (strength - 1)) * startPopulation (strength 1..3). */
  nationStrengthBonus: 0.3,
  /** Bots spawned per tick at the start of the spawn phase (spreads CPU cost). */
  botsPerTick: 25,
  /** Random attempts when looking for a free spawn tile. */
  randomAttempts: 400,
  /** Search radius around a nation's map position for a free spawn tile. */
  nationSearchRadius: 150,
  /** Upper bound for settings.spawnPhaseSeconds. */
  maxSpawnPhaseSeconds: 600,
  /**
   * settings.instantSpawn (singleplayer): the spawn phase ends once every human has spawned, but
   * not before this tick, so the AI players are placed first (botsPerTick per tick).
   */
  instantSpawnMinTicks: 5,
  // --- Wave 4 (12): tribes are hundreds of grey settlements spread evenly over the land ----
  /** Land tiles per tribe: the tribe count is min(settings.bots, land / this). */
  tribeLandPerTribe: 1_000,
  /** A tribe keeps at least this distance to any other territory when it spawns. */
  tribeSpacing: 9,
  /** … and at least this distance to a nation or human. */
  tribeNationGap: 16,
  // --- Wave 4 stage N (firstgame, ai/breathingRoom.ts): room for human players -------------
  /**
   * A nation's spawn keeps this share of sqrt(land tiles) from every human spawn (centre to
   * centre), per difficulty: about 45 / 37 / 22 / 15 tiles on British Isles and the world map.
   */
  humanRoom: { [Difficulty.Easy]: 0.06, [Difficulty.Medium]: 0.05, [Difficulty.Hard]: 0.03, [Difficulty.Impossible]: 0.02 } as Record<Difficulty, number>,
  /** Bounds of that room in tiles (small test maps / very large maps). */
  humanRoomMin: 12,
  humanRoomMax: 64,
};

// =============================================================================
// 3. POPULATION  (maxPop = baseMax + perTileK * tiles^0.75 + cityPerLevel * cityLevels)
// =============================================================================

export const POPULATION = {
  baseMax: 6_000,
  perTileK: 100,
  /** Max population added per completed city level. */
  cityPerLevel: 50_000,
  /** growth/tick = (baseGrowth + pop^0.75 / growthDivisor) * (1 - pop/max) * gameSpeed * aiMult */
  baseGrowth: 10,
  growthDivisor: 4,
  defaultTroopRatio: 0.6,
  minTroopRatio: 0.05,
  maxTroopRatio: 1,
  /** Max fraction of the population moved between troops/workers per tick to follow the ratio. */
  rebalanceFraction: 0.004,
  /** Fraction of the excess above maxPopulation lost per tick. */
  decayFraction: 0.02,
  /** Home troops kept topped-up at this value for humans with settings.infiniteTroops. */
  infiniteTroops: 1_000_000,
};

// =============================================================================
// 4. GOLD
// =============================================================================

export const GOLD = {
  /** gold/tick = perSqrtWorker * sqrt(workers) * gameSpeed * aiGoldMult (+ trade, trains) */
  perSqrtWorker: 2.5,
  startingGold: 0,
  /** Share of a player's gold given to whoever eliminates them. */
  eliminationShare: 0.5,
  /** Gold shown / available for humans with settings.infiniteGold. */
  infiniteGold: 1_000_000_000_000,
};

// =============================================================================
// 5. ATTACK (land)
// =============================================================================

export const ATTACK = {
  /**
   * Unowned land: cost/tile = terrainCost * (neutralBase + neutralShareK * sqrt(attackerTiles / pricedLand)),
   * pricedLand = max(the map's land tiles, neutralShareMinLand). The size part is the "big empire tax"
   * that also floors the price of enemy land. Pace (owner, 2026-10-22; docs/BALANCE.md#pace-history): neutralBase
   * was 6. Pace follow-up (2026-10-22): the tax grows with the attacker's share of the map instead of
   * its tile count (was neutralSizeK × sqrt(attackerTiles), 0.15 before the pace work, then 0.1), so a
   * big map is no slower than a small one at the same share: cheaper on the big real maps, dearer on
   * the procedural ones. Maps smaller than neutralShareMinLand land tiles are priced as that size.
   */
  neutralBase: 5.5,
  neutralShareK: 90,
  neutralShareMinLand: 150_000,
  /**
   * Enemy land: cost/tile = terrainCost * max(neutralCost, neutralCost * enemyNeutralShare +
   * enemyBase + defenderDensity * densityK) * modifiers, where defenderDensity = defender home
   * troops / defender tiles. Never cheaper than unowned land for the same attacker.
   */
  enemyNeutralShare: 0.5,
  enemyBase: 3,
  /** Pace (2026-10-22): was 2 (troops take land cheaper). */
  densityK: 1.8,
  /** Defender troops lost per conquered tile = defenderDensity * defenderLossK. Pace (2026-10-22): was 1.25. */
  defenderLossK: 1.4,
  /** Cost multipliers. */
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 2.5. */
  defendedMult: 3,
  falloutMult: 3,
  traitorDefenderMult: 0.6,
  navalMult: 1.0,
  /** Fraction of the frontier conquered per tick (unowned land). Pace (2026-10-22): was 0.55. */
  neutralFrontSpeed: 0.7,
  /** Fraction of the frontier conquered per tick vs players, scaled by sqrt(force ratio). Pace (2026-10-22): was 0.3. */
  playerFrontSpeed: 0.36,
  forceRatioMin: 0.3,
  /** Pace (2026-10-22): was 2 (a big attack rolls over a weak neighbour faster). */
  forceRatioMax: 2.5,
  /** At most this fraction of the attack's troops is spent per tick. Pace (2026-10-22): was 0.16. */
  troopSpendRate: 0.2,
  minTilesPerTick: 1,
  maxTilesPerTick: 2_500,
  /** Troops lost when retreating (cancelAttack). */
  retreatPenalty: 0.25,
  /** Attacks with fewer troops than this are not started / end. */
  minTroops: 1,
  /** Front shaping (priority queue): jitter amount, bonus per extra attacker neighbour. */
  jitter: 0.6,
  neighborBonus: 0.45,
  // --- Wave 4 (5): the attack leans toward the cursor (stage B retune: broad front) --------
  /**
   * Share of each tick's tile budget that leans toward the targetTile hint (the clicked
   * tile); the rest (≥ 60 %) advances the whole shared border in arrival order. A share that
   * runs dry hands its rest to the other one, so the throughput does not change.
   */
  hintShare: 0,
  /**
   * Owner feedback: even a 40 % directional share runs ahead as a spear and leaves the rest of
   * the border behind. The default is now a tilt of the single broad front instead: a frontier
   * tile's queue key = its arrival time + this × its (route) distance to the hint (a weighted
   * A* wave). The whole border keeps advancing every tick and the territory grows as an egg
   * that bulges toward the click instead of a spear. The tilt is measured from the closest
   * distance to the click the attack has reached so far and capped at hintTiltMax, so the part of
   * the front near the click leads by at most ~hintTiltMax tiles and a long front far from the
   * click keeps (almost) its full pace. 0 = no lean. Must stay < 1.
   */
  hintTiltK: 0.25,
  /** Cap of the tilt (queue-key units ≈ tiles of lead on plains). */
  hintTiltMax: 10,
  /**
   * Owner feedback: territories must look like countries, not balls. A seeded noise field
   * (combat/roughness.ts) scales every tile's front delay by 1 ± this, so fronts grow lobes
   * and inlets along the easy ground. Only the order of conquest changes, not the cost.
   */
  roughness: 0.55,
  /** Owner feedback: attacking land cut off from its owner's capital costs this × (combat/regions.ts). */
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 0.6 (cut-off land is clearly weaker; a city or a sea route keeps it supplied). */
  cutOffDefenseMult: 0.5,
  /**
   * Directional order: a frontier tile's priority in the directional share = its arrival
   * time + this × its (route) distance to the hint. The front's lead toward the click then falls
   * off smoothly with the distance from it (higher = narrower bulge, lower = flatter lean).
   */
  hintDistanceK: 1.0,
  /**
   * Tiles exposed in a tick can be conquered in the same tick only this many generations deep,
   * so the front advances at most this many tiles per tick anywhere (the tip of the lean
   * included) and the rest of the tick's budget widens it (total tiles per tick are unchanged).
   */
  frontStepsPerTick: 1,
  /**
   * Route field: when the straight line from the front to the hint leaves the target's land
   * (water, a third player), the distance to the hint is measured along the target's land by a
   * bounded BFS (passes and isthmuses), within this Chebyshev radius around the hint.
   */
  routeMaxRadius: 220,
  /** Route BFS work (tiles visited) allowed per tick for all attacks; over budget → next tick. */
  routeTilesPerTick: 150_000,
  // --- Wave 4 (8): losses scale with land taken -------------------------------------------
  /**
   * Engaged defenders: only the defender's troops within this many tiles of the front fight
   * an attack; the defender never loses more per tick than those.
   */
  engageDepth: 6,
  /**
   * Trade cap: the defender-density part of a tile's cost is at most this many attacker troops
   * per defender troop killed on that tile (the land part of the cost is always paid).
   */
  lossRatioCap: 6,
  /** An attack that cannot take a tile for this many ticks ends; survivors return without penalty. */
  stallTicks: 30,
  /**
   * An attack that cannot afford its next tile ends at once ("spent", silent) when fewer than
   * this share of its peak troops are left; otherwise it stalls (see stallTicks).
   */
  stallMinShare: 0.25,
  /** Frontier tiles skipped per tick when the best one is too expensive (the front flows around it). */
  blockedSkipMax: 16,
  // --- Wave 4 (6): encirclement -------------------------------------------------------------
  /** Share of the defender's border held by the attacker coalition above which the bonus starts. */
  encircleThreshold: 0.6,
  /** The bonus stops again below threshold − hysteresis (no flapping). */
  encircleHysteresis: 0.05,
  /** At a 100 % share: cost × (1 − encircleCostMax), front speed × (1 + encircleSpeedMax). Linear from the threshold. */
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 0.4. */
  encircleCostMax: 0.5,
  encircleSpeedMax: 0.5,
  /** Coast tile-edges count this much in the defender's border (land edges count 1). */
  encircleCoastWeight: 0.5,
  /** Ticks between two encirclement samples of an attack. */
  encircleSampleTicks: 5,
};

// =============================================================================
// 6. TERRAIN
// =============================================================================

export const TERRAIN = {
  /** Troop cost multipliers per land class. */
  plainsCost: 1,
  highlandCost: 1.6,
  mountainCost: 2.4,
  /** Front propagation delay per land class (how fast the wave advances). */
  plainsDelay: 1,
  highlandDelay: 1.5,
  mountainDelay: 2.2,
  // --- Wave 4 (2): strategic terrain (settings.strategicTerrain; attacks on players only) --
  /**
   * Elevation: magnitude of the target tile minus the highest attacker-owned neighbour.
   * Uphill costs up to +uphillMax (reached at uphillFullDiff), downhill saves up to downhillMax.
   */
  uphillMax: 0.8,
  uphillFullDiff: 4,
  downhillMax: 0.35,
  downhillFullDiff: 4,
  /** Defender bonus on a chokepoint tile at full strength (255). */
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 0.6. */
  chokepointMax: 0.8,
  /** Passages up to this local width (tiles) between water / mountains / the map edge are narrow. */
  chokeNarrowWidth: 4,
  /** Open areas smaller than this do not count as regions on either side of a passage. */
  chokeRegionMinTiles: 60,
  /**
   * Stage I warfare [12] (combat/tactics.ts): a second, wider scale. Passages up to this width
   * between open regions of at least chokeWideRegionMinTiles tiles count too (a pass that looks
   * wide on the map but is narrow relative to the land around it). ≤ chokeNarrowWidth = off.
   */
  chokeWideWidth: 8,
  chokeWideRegionMinTiles: 240,
  /** Island: a land body of at most clamp(islandLandShare × all land, islandMinTiles, islandMaxTiles) tiles. */
  islandLandShare: 0.006,
  islandMinTiles: 150,
  islandMaxTiles: 5_000,
  /** Defender on its home island against an invader from elsewhere: cost × (1 + islandBonus). */
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 0.4. */
  islandBonus: 0.6,
  /** Naval landings: the first tile costs × (1 + beachheadPenalty), falling linearly to ×1 after beachheadTiles. */
  beachheadPenalty: 0.6,
  beachheadTiles: 10,
  /**
   * Flat cost on attacking into a river tile from a non-river tile: cost × (1 + riverCrossing).
   * 0 since the bridges module (docs/GAME.md#rivers-and-bridges): rivers are crossed only over bridges,
   * which already makes them chokepoints; a flat cost on top would count the river twice.
   */
  riverCrossing: 0,
  /** Clamp of all stacked situational multipliers (terrain class, fallout and AI handicap excluded). */
  defenderAdvantageCap: 4,
  attackerAdvantageCap: 2.5,
  /**
   * Stage I warfare [8] (combat/terrainEffects.ts): terrain warfare zones (desert / winter
   * attrition, mud, jungle ambush, forest and mountain cover) price every land attack. Needs
   * settings.strategicTerrain; kill switch for tests / balance runs.
   */
  warfareZones: true,
};

// =============================================================================
// 7. ENCLAVES (annexation of surrounded pockets)
// =============================================================================

/**
 * Regions, capitals and cut-off land (owner feedback, wave 4; combat/regions.ts):
 * "Cut an empire in two and the part without its capital withers."
 */
export const REGIONS = {
  /** Master switch (tests of other systems may turn it off). */
  enabled: true,
  /** Ticks between scans. */
  scanInterval: 10,
  /** Tiles relabelled per scan at most (at least one player per scan is always done). */
  scanBudget: 400_000,
  /** Troops lost per second by a fully cut-off player (scaled by the cut-off share of its land). */
  drainPerSecond: 0.02,
  /** A cut-off piece this small (tiles, or this share of its owner's land) falls at once to an attacker next to it. */
  smallPieceTiles: 40,
  smallPieceShare: 0.04,
  /** A player this small (tiles) that is attacked by a neighbour loses everything at once. */
  tinyPlayerTiles: 25,
  /** A player enclosed by one other player is annexed with at most this many tiles … */
  enclosedTinyTiles: 80,
  /** … or at most this share of the surrounder's land. */
  enclosedTinyShare: 0.03,
  /** Wave 4 P7: cut-off regions per player reported to clients (PlayerUpdate.cutOffSeeds, largest first). */
  summaryPieces: 32,
  /**
   * Stage I warfare [15] (combat/enclaveSupply.ts): a cut-off region holding a completed city, or a
   * port with a sea route to another own port, stays supplied (counted as connected). Kill switch.
   */
  citySupply: true,
};

export const ENCLAVE = {
  /** Ticks between enclave scans. */
  checkInterval: 10,
  /** Pockets up to this many tiles are annexed instantly. */
  smallPocketTiles: 25,
  /** Larger enclaves are annexed after being surrounded for this many ticks. */
  annexDelayTicks: 50,
  /** Regions larger than this are never treated as enclaves. */
  maxEnclaveTiles: 4_000,
};

// =============================================================================
// 8. WIN
// =============================================================================

export const WIN = {
  minPercent: 1,
  maxPercent: 100,
};

// =============================================================================
// 9. NAVAL (transport ships)
// =============================================================================

export const NAVAL = {
  maxTransportShips: 3,
  transportSpeed: 2,
  /** Minimum troops a boat must carry. */
  minBoatTroops: 1,
  /** Coarse navigation grid cell size used by water path finding (2..16). */
  navGridCell: 8,
  /** Max A* expansions before a path search gives up. */
  maxPathNodes: 400_000,
  /** Fraction of troops lost when a boat is cancelled at sea and returns. */
  cancelPenalty: 0,
  /** A boat aimed at a water tile lands on the nearest coast within this radius. */
  targetSnapRadius: 12,
  /** Search radius for a landing tile when the target is unowned land (owned targets: whole coast). */
  landingSearchRadius: 250,
  /** Boat intents per player and tick whose planning may fail; later ones that tick are ignored. */
  maxFailedBoatPlansPerTick: 2,
  /** LRU capacity of the per-game water path cache (trade routes are reused constantly). */
  pathCacheSize: 1024,
  /** Longest line-of-sight smoothing shortcut of a ship path, in tiles (1 = no smoothing). */
  pathSmoothLookahead: 160,
  /**
   * Transport launch site: a completed own port is preferred over a plain shore tile while its
   * route is at most this many tiles longer by water (0 = always the nearest shore).
   */
  portLaunchPreference: 8,
  /**
   * Route safety: when planning transport and trade routes, water within `dangerRadius` of a
   * hostile warship costs (1 + routeDangerCost) times as much (0 = no avoidance).
   */
  routeDangerCost: 2,
  /** Radius (tiles) around hostile warships that routes try to avoid (about their gun range). */
  dangerRadius: 30,
  /** Ships at sea look this many path tiles ahead for hostile warships... */
  replanLookahead: 60,
  /** ...every this many ticks (staggered per ship)... */
  replanCheckTicks: 10,
  /** ...and re-plan around them at most once per this many ticks per ship... */
  replanCooldownTicks: 50,
  /** ...with at most this many re-plan searches per tick (all ships together). */
  maxReplansPerTick: 2,
  /**
   * A hostile warship the current route was planned with (avoided or knowingly crossed, e.g.
   * in a strait) only counts as a new threat at sea once it has moved more than this many tiles.
   */
  replanThreatMoveTiles: 8,
  /**
   * Ship separation: a moving warship / transport / trade ship that would end its step on a
   * tile already holding another ship steps aside to a free neighbouring water tile
   * (starboard first). false = ships may share tiles.
   */
  shipSeparation: true,
};

// =============================================================================
// 10. TRADE (ports & trade ships)
// =============================================================================

export const TRADE = {
  /** Average ticks between trade ship spawns per port level. */
  spawnIntervalTicks: 150,
  spawnJitterTicks: 60,
  maxTradeShipsPerPlayer: 30,
  tradeShipSpeed: 1.5,
  /** gold per completed trade, paid to BOTH owners: base + perTile * pathLength */
  goldBase: 2_000,
  goldPerTile: 50,
  /** Passive gold per tick per completed port level. */
  portGoldPerTick: 5,
  /**
   * Max uncached trade route searches per tick (all ports together). Ports that come due
   * once the budget is used retry next tick; bounds the per-tick cost with hundreds of ports.
   */
  maxRouteSearchesPerTick: 2,
  /** LRU capacity of the trade route cache (one entry per port pair and water body). */
  routeCacheSize: 2048,
  /**
   * A dock occupied by another ship counts this many extra tiles when a new trade ship picks
   * the dock it leaves from: a free dock wins if its route is at most this much longer,
   * otherwise the ship leaves from the occupied dock and steps aside.
   */
  blockedDockCost: 12,
};

// =============================================================================
// 11. WARSHIP
// =============================================================================

export const WARSHIP = {
  maxHealth: 1_000,
  speed: 1,
  patrolRadius: 50,
  targetRange: 60,
  shellRange: 30,
  shellDamage: 250,
  shellSpeed: 6,
  fireCooldownTicks: 20,
  healPerTickInPort: 2,
  maxPerPlayer: 30,
  /** A warship heals when it is within this distance of an own completed port. */
  healRange: 6,
  /** Below this health fraction an idle warship returns to its nearest port to heal. */
  retreatHealthFraction: 0.35,
  /** Enemy trade ships within this distance are captured. */
  captureRange: 8,
  /** Ticks between target (re)acquisitions / chase re-plans. */
  retargetIntervalTicks: 5,
  /** Node budget of patrol / chase path searches (short trips). */
  patrolPathNodes: 30_000,
  /** Random patrol destination attempts before idling. */
  patrolAttempts: 6,
  /** Ticks a warship idles between patrol legs (random in [min, max)). */
  patrolIdleMinTicks: 5,
  patrolIdleMaxTicks: 25,
  /** moveWarship on land / another water body snaps to the nearest reachable water within this radius. */
  moveSnapRadius: 8,
};

// =============================================================================
// 11b. NAVALWAR (wave 4 stage D (3) + P5, module navycontrol; docs/GAME.md#naval-warfare)
//      "Rule the sea to block trade and land armies." Blockades, strait control, sea
//      superiority at landings, convoys / escorts and ship-vs-ship combat. Game setting
//      `navalWarfare` (default on) switches blockades, straits, superiority and convoys off.
// =============================================================================

export const NAVALWAR = {
  // --- blockades (naval/blockade.ts) -------------------------------------------------------
  /** Hostile warships within this many tiles of a port's docks blockade it. */
  blockadeRadius: 12,
  /** Ticks between two blockade checks of a port (staggered by port id). */
  blockadeCheckTicks: 10,
  /** Share of a blockaded port's passive trade income that is lost (1 = all of it). */
  blockadeIncomeCut: 1,
  /**
   * A port is blockaded while the hostile presence near it exceeds its defenders' presence
   * (own + friendly warships) × this factor.
   */
  blockadeDefenseFactor: 1,
  /**
   * Blockades and strait denial only hit enemies at war: players not friendly with an attack
   * between them (either way) within this many ticks (60 s).
   */
  warMemoryTicks: 600,
  /** A port is only blockaded after this many checks in a row find it blockaded (passing ships do not count). */
  blockadeConfirmChecks: 2,
  /** At most one blockade / strait log line per port / (player, strait) within this many ticks (30 s). */
  logCooldownTicks: 300,
  // --- strait control (naval/superiority.ts) ----------------------------------------------
  /** A warship within this many tiles of a strait segment's tiles is present there. */
  straitHoldRadius: 4,
  /** Ticks between two strait-control updates. */
  straitCheckTicks: 10,
  /** Strait tiles are split into segments per square of this many tiles (bounded size). */
  straitSegmentCell: 16,
  /** Blockade bonus: warships holding a strait blockade ports this many times farther away. */
  straitBlockadeMult: 2,
  /** Route cost factor of denied strait cells when an enemy ship looks for a way around (≈ "blocked"). */
  straitDenyCost: 60,
  /**
   * Re-route searches per tick at most for ships at sea whose way a newly held strait denies (the
   * others wait before the denied water for a later tick; a ship turns back only when its own
   * search found no free way). At least 1.
   */
  maxDenyReroutesPerTick: 2,
  // --- sea superiority at landings (naval/superiority.ts → combat/modifiers.ts) ------------
  /** Warships within this many tiles of a landing's water count for sea superiority. */
  superiorityRadius: 20,
  /** Presence advantage (1 = one healthy warship) that gives the full effect. */
  superiorityFullStrength: 2,
  /** Own superiority removes up to this share of the beachhead penalty (1 = all of it). */
  superiorityRelief: 1,
  /** Enemy superiority raises the beachhead penalty by up to this share (1 = doubled; capped). */
  superiorityPenalty: 1,
  // --- convoys and escorts (naval/convoy.ts) ------------------------------------------------
  /** Own boats landing within this many ticks of each other near one landing join one attack. */
  convoyWindow: 60,
  /** …when their landings are at most this many tiles apart. */
  convoyRadius: 15,
  /** An escort keeps within this many tiles of its transport. */
  escortDistance: 3,
  /** An escort chases hostile ships only within this many tiles of its transport. */
  escortLeash: 15,
  /** A transport sails at its slowest escort's speed while an escort is within this range… */
  escortSpeedRange: 6,
  /** …and at half that speed while an escort farther away (up to this range) catches up. */
  escortGatherRange: 40,
  // --- hold orders (warships.ts) ------------------------------------------------------------
  /** A holding warship patrols within this many tiles of its station. */
  holdRadius: 2,
  /** …and chases hostile ships at most this many tiles away from it. */
  holdLeash: 14,
  // --- ship-vs-ship combat (warships.ts, naval/shipStats.ts) ---------------------------------
  /** Highest armor any ship type may have (share of shell damage absorbed). */
  maxArmor: 0.8,
  /** Armor of the default warship. */
  warshipArmor: 0,
  /** Shooters skip targets whose shells in flight already sink them (when another target is in range). */
  focusFire: true,
  // --- AI (ai/nation/seacontrol.ts) ------------------------------------------------------------
  /** Straits within this many tiles of an own port dock are on the nation's sea lanes. */
  aiStraitRange: 140,
  /** A nation needs at least this many healthy warships in a water body before it holds straits. */
  aiStraitMinFleet: 2,
  /** A nation needs at least this many healthy warships in a water body before it blockades. */
  aiBlockadeMinFleet: 2,
  /** Ships below this health share are never sent to hold or blockade. */
  aiMinHealth: 0.6,
  // --- naval stance (stage J, module naval: src/core/naval; docs/GAME.md#automatic-navy-the-naval-stance) ------------
  // "Choose a naval stance; your shipyards build the fleet and it fights by itself." The strait /
  // blockade keys above (aiStraitRange, aiStraitMinFleet, aiBlockadeMinFleet, aiMinHealth) now
  // drive every player's Sea control stance.
  /** Ticks between two production checks of a player (staggered by player id; 0 = no production, scripted tests). */
  stanceProductionTicks: 50,
  /** Ticks between two task plans of a player's fleet (staggered by player id; 0 = no automatic orders, scripted tests). */
  stancePlanTicks: 20,
  /** Orders (each one path search) per plan of one player… */
  stanceOrdersPerPlan: 3,
  /** …and game-wide per tick (the rest waits for the next plan). */
  stanceOrdersPerTick: 8,
  /** Coastal defence: warships per completed port, at most this many (+ coastalWarWarships at war). */
  coastalWarshipsPerPort: 1,
  coastalMaxWarships: 3,
  coastalWarWarships: 2,
  /** Coastal defence: one patrol boat per this many completed ports, at most coastalMaxPatrol. */
  coastalPortsPerPatrol: 2,
  coastalMaxPatrol: 2,
  /** Coastal defence buys a ship only while the gold left afterwards is ≥ this × its price. */
  coastalReserve: 1,
  /** Sea control: warships per completed port, at most this many (+ seaWarWarships at war). */
  seaWarshipsPerPort: 2,
  seaMaxWarships: 8,
  seaWarWarships: 4,
  /** Sea control: patrol boats per completed port, at most seaMaxPatrol. */
  seaPatrolPerPort: 1,
  seaMaxPatrol: 5,
  /** Sea control: battleships per completed shipyard (+1 at war with a coastal enemy), at most seaMaxBattleships. */
  seaBattleshipsPerYard: 1,
  seaMaxBattleships: 4,
  /** Sea control buys a ship only while the gold left afterwards is ≥ this × its price. */
  seaReserve: 0.35,
  /** The reserve a stance keeps after a purchase is at most this much gold (big ships stay affordable). */
  reserveMaxGold: 200_000,
  /** Hostile combat ships within this many tiles of an own port dock or trade ship are engaged. */
  guardRadius: 60,
  /** Hostile transports heading for own land are intercepted within this many tiles. */
  huntRadius: 120,
  /** A ship reaches an intercept point when its straight-line lead is within this many tiles. */
  interceptSlack: 24,
  /** Hostile combat ships within this many tiles of a boat's route / landing threaten it. */
  threatRadius: 45,
  /** Sent strength ≥ the enemy's × this (no suicide runs). */
  engageMargin: 1.2,
  /** A task is re-ordered only when its destination moved more than this many tiles. */
  reorderDistance: 12,
  /** A guard keeps its station while its patrol centre is within this many tiles of it. */
  guardDrift: 30,
  /** Own trade ships with hostile ships near them get an escort (at most this many escorts per player). */
  maxTradeEscorts: 2,
};

// =============================================================================
// 12. BUILD (costs, construction, levels) for every buildable unit type
// =============================================================================

export interface CostSpec {
  /** Cost of the first unit. */
  base: number;
  /** Added per unit/level already owned (incl. under construction). 0 = flat price. */
  increment: number;
  /** Maximum cost. */
  max: number;
}

export interface BuildSpec {
  cost: CostSpec;
  /** Construction time; 0 = instant. Ignored with settings.instantBuild. */
  constructionSeconds: number;
  /** Max level for upgradable structures (1 = cannot be upgraded). */
  maxLevel: number;
  /** City/Port/Factory are captured on conquest; others are destroyed. */
  capturable: boolean;
  /** Must be placed on a shore land tile. */
  requiresShore: boolean;
}

export const BUILD: Record<string, BuildSpec> = {
  [UnitType.City]: { cost: { base: 125_000, increment: 125_000, max: 1_000_000 }, constructionSeconds: 10, maxLevel: 10, capturable: true, requiresShore: false },
  [UnitType.Port]: { cost: { base: 125_000, increment: 125_000, max: 1_000_000 }, constructionSeconds: 10, maxLevel: 10, capturable: true, requiresShore: true },
  // Defense posts: each level widens the defended area (STRUCTURES.defensePostRadiusPerLevel).
  [UnitType.DefensePost]: { cost: { base: 50_000, increment: 50_000, max: 250_000 }, constructionSeconds: 5, maxLevel: 5, capturable: false, requiresShore: false },
  [UnitType.MissileSilo]: { cost: { base: 1_000_000, increment: 0, max: 1_000_000 }, constructionSeconds: 10, maxLevel: 5, capturable: false, requiresShore: false },
  [UnitType.SAMLauncher]: { cost: { base: 1_500_000, increment: 1_500_000, max: 3_000_000 }, constructionSeconds: 12, maxLevel: 5, capturable: false, requiresShore: false },
  [UnitType.Factory]: { cost: { base: 1_000_000, increment: 500_000, max: 3_000_000 }, constructionSeconds: 12, maxLevel: 5, capturable: true, requiresShore: false },
  // Wave 4 P2: boosts own deposits within EXTRACTOR.radius; levels raise the boost (wood makes it cheaper).
  [UnitType.Extractor]: { cost: { base: 100_000, increment: 100_000, max: 600_000 }, constructionSeconds: 8, maxLevel: 5, capturable: true, requiresShore: false },
  // Wave 4 stage E (module airforce, AIRFORCE below): one sortie slot per level; oil (and some
  // wood) make it cheaper; destroyed, not captured, on conquest.
  [UnitType.Airfield]: { cost: { base: 800_000, increment: 400_000, max: 2_400_000 }, constructionSeconds: 15, maxLevel: 5, capturable: false, requiresShore: false },
  [UnitType.Warship]: { cost: { base: 250_000, increment: 250_000, max: 1_000_000 }, constructionSeconds: 0, maxLevel: 1, capturable: false, requiresShore: false },
  // Wave 4 P2 (module ships, docs/GAME.md#automatic-navy-the-naval-stance): a cheap fast hunter at any port, an expensive
  // battleship at a shipyard, and the shipyard itself (a shore structure next to an own port).
  [UnitType.PatrolBoat]: { cost: { base: 100_000, increment: 50_000, max: 400_000 }, constructionSeconds: 0, maxLevel: 1, capturable: false, requiresShore: false },
  [UnitType.Battleship]: { cost: { base: 1_500_000, increment: 1_000_000, max: 4_500_000 }, constructionSeconds: 0, maxLevel: 1, capturable: false, requiresShore: false },
  [UnitType.Shipyard]: { cost: { base: 750_000, increment: 750_000, max: 2_250_000 }, constructionSeconds: 20, maxLevel: 1, capturable: true, requiresShore: true },
  [UnitType.AtomBomb]: { cost: { base: 750_000, increment: 0, max: 750_000 }, constructionSeconds: 0, maxLevel: 1, capturable: false, requiresShore: false },
  [UnitType.HydrogenBomb]: { cost: { base: 5_000_000, increment: 0, max: 5_000_000 }, constructionSeconds: 0, maxLevel: 1, capturable: false, requiresShore: false },
  [UnitType.MIRV]: { cost: { base: 35_000_000, increment: 0, max: 35_000_000 }, constructionSeconds: 0, maxLevel: 1, capturable: false, requiresShore: false },
};

// =============================================================================
// 13. STRUCTURES (effects)
// =============================================================================

export const STRUCTURES = {
  /** Minimum distance between two structures (any owner). */
  minSpacing: 8,
  /** Defense post: tiles within this radius of an own completed post get the defended bit. */
  defensePostRadius: 30,
  /** Extra defended radius per defense post level above 1 (level 5: 30 + 4 × 5 = 50). */
  defensePostRadiusPerLevel: 5,
  /** Upgrades take this fraction of the construction time (0 with instantBuild)… */
  upgradeTimeFraction: 0.5,
  /** …plus this fraction of it again for every level above 2 (level 3: ×1.2, level 10: ×2.6). */
  upgradeTimePerLevel: 0.2,
  /**
   * Running construction / upgrade countdowns are re-sent to clients on ticks divisible by this
   * (besides start, finish and other changes); clients extrapolate in between (protocol.ts
   * UnitUpdate). 0 = only start / finish.
   */
  progressSyncIntervalTicks: 10,
  /** Gold refunded when deleting an own structure. */
  deleteRefundFraction: 0,
  /** Missile silo reload time after a launch (per silo; each level adds one launch slot). */
  siloReloadSeconds: 10,
};

// =============================================================================
// 14. RAIL (factories & trains)
// =============================================================================

export const RAIL = {
  /** Factories connect to own/allied cities, ports and factories within this distance. */
  connectRadius: 100,
  /** Max tiles of one rail (8-connected polyline over own/allied land, both stations included). */
  maxSegmentTiles: 160,
  /** Max rails attached to one factory (nearest stations are connected first). */
  maxLinksPerFactory: 6,
  /** Rail path searches per tick for the whole game (spreads the CPU cost). */
  maxSearchesPerTick: 2,
  /** Node expansions before one rail path search gives up. */
  maxSearchNodes: 20_000,
  /** Factories scanned for missing connections per tick. */
  maxScansPerTick: 8,
  /** Every completed factory is re-scanned for missing connections this often. */
  rescanIntervalTicks: 50,
  /** A failed connection between two stations is retried after this many ticks. */
  retryTicks: 300,
  /** Rails are re-validated (endpoint owners, alliances, allied land) this often. */
  validateIntervalTicks: 10,
  /** A factory sends a train every interval / level ticks (round trip to one of its stations). */
  trainSpawnIntervalTicks: 150,
  /** Tiles per tick. */
  trainSpeed: 2,
  /** Paid to the train owner at every stop: goldPerStop + goldPerTile * tiles travelled. */
  goldPerStop: 8_000,
  goldPerTile: 40,
  /** A train stopping at an ally's / teammate's station also pays that player this share. */
  allyStationShare: 0.5,
  maxTrainsPerPlayer: 20,
};

// =============================================================================
// 15. NUKES
// =============================================================================

export interface NukeSpec {
  /** Tiles within innerRadius always lose ownership. */
  innerRadius: number;
  /** Tiles between inner and outer radius lose ownership with outerChance. */
  outerRadius: number;
  outerChance: number;
  /** Fraction of tiles in the blast that receive fallout. */
  falloutChance: number;
}

export const NUKES = {
  [UnitType.AtomBomb]: { innerRadius: 12, outerRadius: 28, outerChance: 0.5, falloutChance: 0.6 } as NukeSpec,
  [UnitType.HydrogenBomb]: { innerRadius: 60, outerRadius: 95, outerChance: 0.5, falloutChance: 0.6 } as NukeSpec,
  [UnitType.MIRVWarhead]: { innerRadius: 9, outerRadius: 16, outerChance: 0.5, falloutChance: 0.6 } as NukeSpec,
  /** MIRV: number of warheads and spread radius around the target player territory. */
  mirvWarheads: 120,
  mirvSpreadRadius: 180,
  /** MIRV splits into its warheads this many ticks before its own impact tick. */
  mirvSplitTicks: 40,
  /** Warhead flight: distance / flightSpeed, at least this many ticks (+ seeded jitter). */
  mirvWarheadMinFlightTicks: 20,
  mirvWarheadJitterTicks: 10,
  /** Best-effort minimum distance between two warhead aim points (tiles). */
  mirvMinSpacing: 8,
  /** Flight speed in tiles per tick; flight ticks clamped to [min, max]. */
  flightSpeed: 8,
  minFlightTicks: 30,
  maxFlightTicks: 300,
  /** Troops of an affected player lost proportionally: lostTiles/ownedTiles * troops * mult. */
  troopLossMult: 1,
  /** Relation penalty for nations near a blast (and for the victim). */
  relationPenaltyVictim: -80,
  relationPenaltyNearby: -30,
  /** Nations owning land within outerRadius + this distance count as "near the blast". */
  relationNearbyRadius: 40,
  /**
   * Big blasts release their tiles as a shock wave (nearest first): at most this many owned
   * tiles change owner per tick (keeps a hydrogen bomb's impact tick cheap)…
   */
  blastTilesPerTick: 5_000,
  /**
   * …except the impact tick of such a blast, which releases only this many (nearest) tiles: it
   * already scans the whole disc and destroys the units, and a smaller first batch also keeps
   * the first blast of a session (code not yet optimised by the JIT) cheaper…
   */
  blastImpactTiles: 1_500,
  /**
   * …spread over at most this many ticks (the impact tick included). A blast hitting more than
   * blastImpactTiles + blastTilesPerTick × (blastMaxTicks − 1) tiles uses bigger chunks instead
   * of more ticks.
   */
  blastMaxTicks: 5,
};

// =============================================================================
// 16. SAM
// =============================================================================

export const SAM = {
  /** A SAM engages hostile missiles whose target tile lies within this distance. */
  range: 80,
  /** Reload time of one interception slot (a SAM has one slot per level). */
  cooldownSeconds: 7.5,
  /** SAM missile speed (tiles per tick). */
  missileSpeed: 14,
  /** Chance to hit (1 = always) per interception. */
  hitChance: 1,
  /** Interceptions are planned to hit this many ticks before the missile's impact. */
  interceptLeadTicks: 2,
};

// =============================================================================
// 17. FALLOUT
// =============================================================================

/** Wave 4 fallout: the crater a nuke type leaves (FALLOUT.craters). */
export interface CraterSpec {
  /** Land within this radius (tiles) of ground zero is lowered: mountains flatten. */
  radius: number;
  /** Share of a tile's elevation removed at ground zero (falls to 0 at the crater rim). */
  flatten: number;
  /** Land within this radius turns into a crater lake (0 = none). */
  lakeRadius: number;
  /** Depth of the crater lake at ground zero (1 at its shore). */
  lakeDepth: number;
}

/** Wave 4 fallout: the aftermath strength of a nuke type (FALLOUT.effects). */
export interface AftermathSpec {
  /** Weight towards a nuclear winter (see winterThreshold). */
  winterWeight: number;
  /** Growth drop of every victim country (stacks per hit up to panicMaxDrop). */
  panicDrop: number;
  /** EMP: seconds the structures around the blast stop working. */
  empSeconds: number;
  /** Relation change of every other nation toward the launcher (a MIRV salvo counts once). */
  anger: number;
}

export const FALLOUT = {
  /** Ticks between decay passes over fallout tiles. */
  decayInterval: 10,
  /** Chance per pass that a fallout tile is cleared (~2–3 min average lifetime). */
  decayChancePerPass: 10 / 1500,

  // Wave 4 stage D (4), module fallout (docs/TECHNICAL.md#live-terrain, feature toggle `nukeEffects`):
  // "Nukes scar the land, the economy and the whole world."
  /** Craters per nuke type (MIRV warheads: small ones; the MIRV itself never explodes). */
  craters: {
    [UnitType.AtomBomb]: { radius: 7, flatten: 0.7, lakeRadius: 0, lakeDepth: 0 } as CraterSpec,
    [UnitType.HydrogenBomb]: { radius: 22, flatten: 0.85, lakeRadius: 6, lakeDepth: 3 } as CraterSpec,
    [UnitType.MIRVWarhead]: { radius: 4, flatten: 0.5, lakeRadius: 0, lakeDepth: 0 } as CraterSpec,
  } as Record<string, CraterSpec>,
  /** Aftermath per nuke type. */
  effects: {
    [UnitType.AtomBomb]: { winterWeight: 10, panicDrop: 0.15, empSeconds: 30, anger: -10 } as AftermathSpec,
    [UnitType.HydrogenBomb]: { winterWeight: 30, panicDrop: 0.25, empSeconds: 45, anger: -20 } as AftermathSpec,
    [UnitType.MIRVWarhead]: { winterWeight: 1, panicDrop: 0.02, empSeconds: 15, anger: -25 } as AftermathSpec,
  } as Record<string, AftermathSpec>,
  /** Deposits inside the blast (outer radius) are contaminated this long: no deposit bonus. */
  contaminationSeconds: 240,
  /** EMP radius = the blast's outer radius × this (structures inside the blast are destroyed anyway). */
  empRadiusMult: 1.6,
  /** A victim country panics this long after a hit (each hit restarts the timer). */
  panicSeconds: 60,
  /** Largest growth drop from panic. */
  panicMaxDrop: 0.3,
  /** Nuclear winter: when the weight of nukes detonated within winterWindowSeconds reaches winterThreshold… */
  winterWindowSeconds: 120,
  winterThreshold: 80,
  /** …every country grows this much slower for winterSeconds (a new trigger restarts it). */
  winterSeconds: 180,
  winterGrowthDrop: 0.2,
  /** Cap: panic and nuclear winter together never push growth below this multiplier. */
  minGrowthMult: 0.7,
  /** AI: an expansion / attack aim within this many tiles of fallout is avoided while another aim exists. */
  aiAvoidRadius: 6,
  /** AI: a nation launches no nuke while its average standing among nations is below this (world anger). */
  aiAngerLimit: -45,
};

// =============================================================================
// 18. DIPLOMACY
// =============================================================================

export const DIPLOMACY = {
  allianceDurationTicks: secondsToTicks(5 * 60),
  allianceRequestTimeoutTicks: secondsToTicks(20),
  /** Extension can be requested during the last N ticks of an alliance. */
  allianceExtensionWindowTicks: secondsToTicks(30),
  /** Cooldown before re-sending a request to the same player after a rejection. */
  allianceRequestCooldownTicks: secondsToTicks(30),
  traitorDurationTicks: secondsToTicks(60),
  /** Relation score bounds and defaults (nation AI). */
  relationMin: -100,
  relationMax: 100,
  relationDefault: 0,
  /** Max troops ratio per donation; gold donations limited to own gold. */
  maxDonationRatio: 1,
  donationCooldownTicks: secondsToTicks(2),
  maxTargets: 5,
  /**
   * Nation relation changes (applied by src/core/executions/diplomacy, see
   * docs/GAME.md#alliances-and-the-basics). Only Nation players hold relation scores.
   */
  /** Both sides when an alliance is formed. */
  relationAllianceFormed: 25,
  /** Both sides when an alliance is extended. */
  relationAllianceExtended: 10,
  /** Requester towards the player that rejected its alliance request. */
  relationAllianceRejected: -8,
  /** Victim towards the traitor that broke the alliance. */
  relationBetrayalVictim: -80,
  /** Every other nation towards a traitor. */
  relationBetrayalWitness: -20,
  /** Victim towards the attacker, once per new land / naval attack. */
  relationAttacked: -12,
  /** Victim towards the attacker when nuked (helper for the nukes module / AI). */
  relationNuked: -60,
  /** Max gain of the receiver per donation (full gain at >= 25% of its own stock). */
  relationDonationMax: 15,
  /** Target towards the player that placed an embargo on it (and back when lifted). */
  relationEmbargo: -15,
  relationEmbargoLifted: 8,
  /** Read-time malus (effectiveRelation) towards players that are currently traitors. */
  relationTraitorMalus: 30,
  /** Relations drift towards relationDefault by relationDriftStep every interval… */
  relationDriftIntervalTicks: secondsToTicks(5),
  relationDriftStep: 1,
  /** …except towards allies, which gain relationAllyDrift per interval. */
  relationAllyDrift: 1,
};

// =============================================================================
// 19. SOCIAL (emoji, chat, pings)
// =============================================================================

export const SOCIAL = {
  emojiCooldownTicks: secondsToTicks(2),
  quickChatCooldownTicks: secondsToTicks(2),
  chatCooldownTicks: secondsToTicks(1),
  chatMaxLength: 200,
  pingCooldownTicks: secondsToTicks(1.5),
};

// =============================================================================
// 19a. ALLIED WARS (wave 4 stage C part 2, module allywar; docs/GAME.md#allied-wars-coalitions-and-standing)
// =============================================================================

/**
 * "Ask your ally to attack, and fight side by side for a bonus." War requests between allies /
 * teammates, nations joining their friends' wars, the coalition bonus, relation gains for help
 * and the standing table ("See how every nation feels about you.").
 */
export const ALLYWAR = {
  // --- requests (src/core/executions/diplomacy/warRequests.ts) ---------------------------------
  /** Ticks a war request waits for an answer. */
  requestTimeoutTicks: secondsToTicks(25),
  /** Ticks before the same player may ask the same ally again (any answer). */
  requestCooldownTicks: secondsToTicks(45),
  /** Most open (unanswered) war requests one player may have at a time. */
  maxOpenRequests: 3,
  /** Ticks a nation fights for an ally after accepting a request or joining on its own. */
  joinTicks: secondsToTicks(120),
  /** Ticks between two joinWar announcements of the same player. */
  joinCooldownTicks: secondsToTicks(30),
  // --- coalition bonus (combat/modifiers.ts; part of the situational clamp) -------------------
  /** Attack cost × (1 − coalitionBonus) while an ally attacks the same target (capped at coalitionCap). */
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 0.15. */
  coalitionBonus: 0.25,
  /** Front speed × (1 + coalitionSpeed) in a coalition (capped at coalitionCap). */
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 0.15. */
  coalitionSpeed: 0.25,
  /** Hard cap of both coalition terms (the cost multiplier never drops below 1 − cap = 0.7). */
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 0.3. */
  coalitionCap: 0.4,
  /** Ticks between two coalition samples (who attacks the same target as a friend). */
  coalitionSampleTicks: 5,
  /** A coalition with the same partner against the same target is announced at most this often. */
  coalitionAnnounceTicks: secondsToTicks(90),
  /**
   * A real commitment (followups): a friend counts as a coalition partner only while its attacks
   * on the target hold at least this share of its troops (home + those attacks) …
   */
  minCommitShare: 0.1,
  /** … or at least this many troops (a big empire need not send a tenth of its army). */
  minCommitTroops: 50_000,
  // --- help (relation gains) ------------------------------------------------------------------
  /** Relation gain of a nation when a friend attacks the player that is attacking it. */
  relationHelpDefend: 10,
  /** Relation gain when a friend joins our own offensive or keeps its promise (pact). */
  relationHelpOffense: 5,
  /** Relation gain of the requester (nation) when its request is accepted. */
  relationRequestAccepted: 4,
  /** Ticks between two help gains of the same nation toward the same helper. */
  helpCooldownTicks: secondsToTicks(60),
  /** A help attack must commit at least this share of the helper's troops (no 1-troop farming). */
  helpMinShare: 0.05,
  // --- standing (PlayerUpdate.standings, human players only) ----------------------------------
  /** Ticks between two refreshes of the standing table. */
  standingIntervalTicks: 10,
  /** Effective relation thresholds between the 5 levels: hostile | wary | neutral | friendly | close. */
  standingThresholds: [-50, -15, 15, 50] as readonly number[],
  /** "Attacked us recently": an attack within this many ticks. */
  recentAttackTicks: secondsToTicks(90),
  /** "Helped us": a help event within this many ticks. */
  helpMemoryTicks: secondsToTicks(150),
  /** "Trade partner": a trade ship between the two within this many ticks. */
  tradeMemoryTicks: secondsToTicks(120),
  // --- AI (src/core/ai/nation/allywar.ts) -----------------------------------------------------
  /** Decision score a nation needs to accept a war request. */
  aiAcceptScore: 12,
  /** Below this effective relation toward the requester a nation never accepts ("no trust"). */
  aiMinTrust: -5,
  /** At or above this relation toward the target a nation refuses to attack it ("we like them"). */
  aiLikesTarget: 35,
  /** Own attack force / target troops below which a nation refuses ("too strong"). */
  aiMinStrength: 0.45,
  /** Relation toward an ally a nation needs to join that ally's wars on its own. */
  aiJoinRelation: 30,
  /** …and to defend an ally that is attacked (lower: defending is a duty). */
  aiDefendRelation: 15,
  /** Base chance per decision (× teamwork) to join an ally's offensive on its own. */
  aiJoinChance: 0.25,
  /** Base chance per decision (× teamwork, ≥ 0.5) to defend an attacked ally. */
  aiDefendChance: 0.7,
  /** Decisions between two "join on our own" checks of a nation. */
  aiJoinEveryDecisions: 3,
  /** Incoming troops ≥ this × home troops: a nation asks its allies for help. */
  aiHelpThreat: 0.4,
  /** A nation asks for help at most once per this many ticks. */
  aiAskGapTicks: secondsToTicks(60),
  /** Share of home troops sent per pact attack (× personality attack ratio, capped). */
  aiPactAttackShare: 0.9,
  /** Alliance requests to the same player that went unanswered / were declined: backoff base ticks. */
  aiAllyBackoffTicks: secondsToTicks(60),
  /** …doubling per unanswered request, at most this long. */
  aiAllyBackoffMaxTicks: secondsToTicks(600),
};

// =============================================================================
// 19b. PORT WATER & SEA STRAITS (wave 4 P4 + P5, module portstraits)
//      docs/GAME.md#ports-trade-and-rails
// =============================================================================

/** Where ports may stand (P4): "Ports only work on real coasts." */
export const PORTS = {
  /**
   * An ocean water body counts as the open sea when it has at least this many tiles; smaller
   * ocean fragments (a bay cut off by the map edge, an enclosed sea at map resolution) are
   * treated like lakes.
   */
  seaMinTiles: 300,
  /**
   * A lake (or small sea fragment) is large enough for a port from this many tiles on — and
   * only when another player's land also borders it at build time.
   */
  largeLakeMinTiles: 150,
  /**
   * River ports (stage C part 2): "Ports work on rivers that flow into the sea." A port may stand
   * on or next to a river tile at most this many river tiles upstream of a mouth into the open
   * sea or a large lake (structures/riverPorts.ts). Tile counts follow the map's resolution
   * (≈ 1 600 km on the world map, ≈ 400 km on the regional maps).
   */
  riverMaxDistance: 80,
  /** Ships sail at this fraction of their sea speed on a river (the launch / arrival delay). */
  riverSpeedFactor: 0.5,
  /** Ticks between two checks whether a river port's river runs through enemy land (log on change). */
  riverCheckIntervalTicks: 10,
};

/** Sea straits (P5): "Whoever controls a strait controls the sea lane." */
export const STRAITS = {
  /** Water passages up to this local width (tiles) between land are narrow. */
  narrowWidth: 6,
  /** Open water areas smaller than this do not count as water regions on either side of a strait. */
  regionMinTiles: 150,
  /**
   * A narrow passage between two different landmasses of at least this many tiles each is a
   * strait even when the water on both ends is the same region (Dover, Malacca).
   */
  flankMinTiles: 400,
  /** Stage I warfare [12]: the wider scale of straits (see TERRAIN.chokeWideWidth); ≤ narrowWidth = off. */
  wideWidth: 10,
  wideRegionMinTiles: 400,
  wideFlankMinTiles: 800,
};

// =============================================================================
// 19b2. BRIDGES (wave 4, module bridges; docs/GAME.md#rivers-and-bridges)
// =============================================================================

/**
 * "Rivers can only be crossed over bridges; your first crossing builds one, and nukes destroy
 * them." Active with settings.strategicTerrain on maps that have rivers.
 */
export const BRIDGES = {
  /** Kill switch for balance runs and A/B perf tests (true in every real game). */
  enabled: true,
  /** Ticks an attack needs to build a bridge (10 ticks = 1 s). */
  buildTicks: 50,
  /**
   * No new bridge within this Euclidean distance (tiles) of an existing bridge the attack can
   * use (on its own or the target's land) or of any bridge under construction: the front has to
   * go over that bridge instead.
   */
  spacing: 16,
  /**
   * An attack that has not taken a tile for this many ticks while a usable bridge lies within
   * `spacing` ignores the target's bridges for the spacing rule (the front cannot reach them)
   * and builds its own.
   */
  stuckTicks: 20,
  /** Ticks between two checks of an attack's blocked river crossings. */
  checkTicks: 5,
  /**
   * An attack whose only way on is a bridge it may not start yet (maxBuildingPerPlayer reached, or
   * another construction within spacing) keeps its troops and waits at most this long (ticks).
   */
  maxWaitTicks: 300,
  /** Most bridges one player builds at the same time. */
  maxBuildingPerPlayer: 3,
  /** Most blocked river tiles an attack keeps track of (bridge candidates). */
  maxEdgeTiles: 4096,
  // --- stage I warfare [13]: the bridge holder fights better around it (combat/pricing.ts) ---
  /** Radius (tiles, ≤ spacing) around a held bridge where its holder's bonus applies. */
  ownerRadius: 8,
  /** Attacks of the holder on enemy tiles there cost × (1 − ownerAttack) … */
  ownerAttack: 0.15,
  /** … and attacks on the holder's tiles there cost × (1 + ownerDefense). */
  ownerDefense: 0.2,
  /**
   * Widest river (tiles, straight across along a row or column) a bridge can span (followups):
   * on a river 2+ tiles wide the bridge stands on the far-side river tile, reached across the
   * river from the attacker's own bank. Wider stretches cannot be bridged (the editor warns).
   */
  maxSpan: 5,
  // --- AI (ai/nation/military.ts) ---------------------------------------------------------
  /** Own border tiles sampled per decision to find river borders. */
  aiBorderSamples: 256,
  /** Share of blocked (river) contacts above which a border counts as a river border. */
  aiRiverShare: 0.6,
  /** Force margin multiplier against a target across a river without a usable bridge. */
  aiRiverMargin: 1.4,
  /** … and with a usable bridge (the front funnels over it). */
  aiBridgeMargin: 1.1,
  /** Score bonus for a target holding a bridge next to our land (take the bridgehead). */
  aiBridgeheadScore: 0.6,
};

// =============================================================================
// 19c. CLIMATE, RESOURCES, SEASONS (wave 4 stage C, module economy)
//      docs/GAME.md#climate-seasons-and-spawn-bonus. Per-climate tables are indexed by the Climate enum:
//      [temperate, desert, tundra, tropical, steppe]; per-season tables by Season:
//      [spring, summer, autumn, winter].
// =============================================================================

/** "Each climate changes how fast land grows and how hard it is to fight in." (settings.climate) */
export const CLIMATE = {
  /** Multiplier on the land part of max population for tiles of each climate. */
  pop: [1, 0.6, 0.55, 1.25, 0.9],
  /** Growth multiplier of each climate (weighted by the player's tiles). */
  growth: [1, 0.85, 0.8, 1.1, 1],
  /** Troop cost multiplier of attacking / expanding into a tile of each climate. */
  cost: [1, 1.15, 1.1, 1.05, 0.95],
  /** Front speed weight of a tile of each climate (> 1 = the front advances slower there). */
  delay: [1, 1.25, 1.15, 1.15, 0.8],
  /** Defender multiplier on owned tiles of each climate (tropical jungle helps defenders). */
  defense: [1, 1, 1, 1.25, 1],
  /**
   * Home climate (the spawn tile's climate): its climate penalties do not apply to the player
   * (pop / growth below 1 and cost / speed weight / jungle defence above 1 become 1), and
   * seasonal penalties on home-climate tiles are scaled by this factor.
   */
  homeSeasonPenalty: 0.5,
  /** Clamp of a player's population and growth multipliers (climate × season × resources). */
  minPopMult: 0.4,
  maxPopMult: 1.6,
  minGrowthMult: 0.3,
  maxGrowthMult: 1.8,
  /** Clamp of the per-tile front speed weight (climate × season × animals). */
  minSpeedWeight: 0.5,
  maxSpeedWeight: 2.5,
};

/** "Where you start shapes your people." Spawn bonus (feature 10). */
export const SPAWN_BONUS = {
  /** Highland start: at least this share of the land within highlandRadius of the spawn is highland / mountain. */
  highlandRadius: 4,
  highlandShare: 0.5,
  /** Highland start: defender bonus on own tiles within capitalRadius of the spawn tile (settings.climate + strategicTerrain). */
  capitalRadius: 22,
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 0.3. */
  highlandFortress: 0.6,
  /**
   * Island start (the spawn's land body is a small island, same size rule as the strategic
   * terrain island flag): defender bonus on the home island against invaders from elsewhere
   * (settings.climate + strategicTerrain).
   */
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 0.25. */
  islandFortress: 0.5,
  /** Island start: a free port on the first valid coast tile the player owns near its spawn (humans and nations; tribes never sail). */
  islandFreePort: true,
  /** Search radius (tiles from the spawn) for the free port's site. */
  freePortRadius: 30,
  /**
   * The start shape rarely reaches the coast, so the free port is retried (every
   * LEVEL_REFRESH_TICKS) for this long after the spawn bonus is fixed: it appears as soon as the
   * player owns a valid coast tile within freePortRadius of its spawn.
   */
  freePortGraceSeconds: 180,
};

/** "Own deposits for bonuses; trade with those who have what you lack." (settings.resources) */
export const RESOURCES = {
  /** Bonus level of n owned deposit tiles = sqrt(n) / (sqrt(n) + sqrt(halfTiles)) (0.5 at halfTiles). */
  halfTiles: 25,
  /** Allies / teammates without an embargo either way add this share of each other's levels … */
  allyShare: 0.25,
  /** … up to this much in total (the level itself stays ≤ 1). */
  allyShareMax: 0.5,
  /**
   * Bonuses at level 1 (linear in the level). Softened after the wave 4 review: with growth
   * +25 % / attack cost -15 % / front speed +20 % the nations ate the tribes far faster
   * (docs/BALANCE.md#economy-findings); stage F re-tunes with more seeds and maps.
   */
  grainGrowth: 0.15,
  grainPop: 0.1,
  /** Cheaper cities, ports and (half) warships. */
  woodBuild: 0.3,
  /** Cheaper defense posts and SAM launchers … */
  stoneBuild: 0.3,
  /** … and defended tiles (inside a defense post's range) are harder to take. */
  stoneDefense: 0.25,
  /** Lower troop cost of every attack (an attacker advantage, clamped with the others). */
  metalAttack: 0.1,
  metalWarship: 0.25,
  /** Cheaper factories, missile silos and missiles. */
  oilBuild: 0.25,
  /** Faster attack fronts (horses) and a little growth (food). */
  animalsSpeed: 0.12,
  animalsGrowth: 0.05,
  /** Total discount of one build price is capped here. */
  maxBuildDiscount: 0.5,
  /**
   * Trade ships between partners with complementary resources earn more:
   * gold × min(maxTradeMult, 1 + tradeComplement × complementarity), complementarity = the
   * mean absolute difference of the two players' own levels (0..1).
   */
  tradeComplement: 1.5,
  maxTradeMult: 2,
};

/** "Seasons change the battlefield every few minutes." (settings.seasons) */
export const SEASONS = {
  /** Length of one season at gameSpeed 1 (seconds); a faster game speed shortens it. */
  seasonSeconds: 240,
  /** Shortest season in ticks (very high game speeds). */
  minSeasonTicks: 300,
  /** Length of the day-night cycle (seconds; visual only). */
  daySeconds: 120,
  /** Growth multiplier per season and climate (a mild spring: everybody grows, the big grow most). */
  growth: [
    [1.1, 1.05, 1.05, 1.05, 1.1],
    [1.05, 0.85, 1.1, 1, 1],
    [1, 1, 0.95, 1, 1],
    [0.8, 0.95, 0.55, 1, 0.8],
  ],
  /** Troop cost multiplier per season and climate (spring mud, summer heat, winter cold). */
  cost: [
    [1.05, 1, 1, 1, 1],
    [1, 1.25, 1, 1, 1],
    [1, 1, 1, 1, 1],
    [1.1, 1, 1.35, 1, 1.1],
  ],
  /** Front speed weight per season and climate (> 1 = slower). */
  delay: [
    [1.25, 1, 1.05, 1, 1],
    [1, 1.3, 0.95, 1, 1],
    [1, 1, 1, 1, 1],
    [1.15, 1, 1.6, 1, 1.15],
  ],
  /** Autumn harvest (with settings.resources): harvestGold × grain tiles^0.75 × gameSpeed × AI gold multiplier, once at autumn start. */
  harvestGold: 8_000,
};

/**
 * "Build near resources and you profit from them." (wave 4 P2, src/core/economy/extractor.ts;
 * needs settings.resources). Every deposit tile the owner holds within `radius` of a completed
 * extractor counts 1 + boost (+ boostPerLevel per level above 1) tiles for the resource levels
 * and the harvest; a tile covered by several extractors counts once (the best one).
 */
export const EXTRACTOR = {
  /** Euclidean radius (tiles) of the boosted area. */
  radius: 13,
  /** No other extractor (any owner) may stand within this distance (one extractor per area). */
  spacing: 13,
  /** Extra weight of a boosted deposit tile at level 1 (it counts 1 + boost tiles). */
  boost: 1,
  /** Extra weight per level above 1 (level 5: 1 + 1 + 4 × 0.25 = 3 tiles). */
  boostPerLevel: 0.25,
  /** Cap of the extra weight. */
  maxBoost: 2,
  // --- nation AI (ai/nation/building.ts) ---
  /** Own deposit tiles before a nation considers its first extractor … */
  aiMinDeposits: 10,
  /** … one more extractor per this many own deposit tiles (at most aiMax). */
  aiDepositsPerExtractor: 20,
  aiMax: 6,
  /** A site must boost at least this many own deposits that no own extractor covers yet … */
  aiMinCover: 6,
  /** … and reaches the full build weight at this many. */
  aiFullCover: 20,
  /** Build weight at full cover (a wanted city scores ≈ 1.25–2, a factory ≈ 0.6). */
  aiWeight: 0.8,
  /** Own tiles sampled (stride) for uncovered deposits, and candidate sites scored, per decision. */
  aiScanTiles: 2048,
  aiSites: 40,
};

// =============================================================================
// 19e. CAPITAL, PLUNDER (wave 4 P7 + P3, module capital; docs/GAME.md#capitals)
// =============================================================================

/**
 * "Your capital is where you spawned, and it is a building." (src/core/empire/capital.ts)
 * Every player gets a free, unique Capital structure on its spawn tile. Land without a land
 * connection to it is cut off (REGIONS).
 *
 * Stage J (docs/GAME.md#capitals): "A capital falls once." Whoever takes it plunders it once and
 * holds it as a captured capital (no relocation, no shield). The player who lost it suffers
 * "Capital lost" until it takes it back; a few minutes after the fall it may declare one of its
 * cities a provisional capital for gold (halves the penalty; falls once too). Retaking the original
 * gives "Liberation" (the penalty ends, a short attack and growth boost).
 */
export const CAPITAL = {
  /** Kill switch (tests / balance runs): without it nobody gets a capital building (regions fall back to the spawn tile). */
  enabled: true,
  /** Max population: each capital level counts as this many completed city levels (POPULATION.cityPerLevel each) … */
  cityLevels: 1,
  /**
   * … for a tribe this many (balance: a full city level per tribe made the hundreds of tribes
   * fortresses — 2.5× the troops, 4× as many alive after 5 minutes on the world map; see
   * docs/GAME.md#capitals).
   */
  tribeCityLevels: 0.25,
  /** Other structures may be built this close to a capital (STRUCTURES.minSpacing does not apply to it). */
  buildSpacing: 3,
  /** Own tiles within this Euclidean radius (tiles) of the capital defend better … */
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 10. */
  defenseRadius: 16,
  /** … attackers pay × (1 + defenseBonus) there (part of the clamped situational product, TERRAIN caps). Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 0.25. */
  defenseBonus: 0.6,
  // --- stage J: "Capital lost" (the player whose capital fell, until it is back) -------------
  /** Troop growth × this while the capital is lost (−30 %; clamped 0.25 .. 1). */
  lostGrowthMult: 0.7,
  /** Gold income × this while the capital is lost (−25 %; clamped 0.25 .. 1). */
  lostGoldMult: 0.75,
  /** Defence: attackers pay × this on the player's land while the capital is lost (−15 %; clamped 0.5 .. 1). */
  lostDefenseMult: 0.85,
  /** More unrest: this many unrest points (0..100) are added to the target of every chunk of the player (scaled by its size like cut-off land; only with unrest on). */
  lostUnrest: 20,
  // --- stage J: provisional capital -------------------------------------------------------
  /** A provisional capital may be declared this long after the fall (seconds). */
  provisionalDelaySeconds: 120,
  /** It costs this much gold. */
  provisionalGold: 150_000,
  /** The Capital lost penalties are scaled by this while a provisional capital stands (0.5 = halved). */
  provisionalDebuffShare: 0.5,
  // --- stage J: Liberation (retaking the original capital) ----------------------------------
  /** Duration of the Liberation boost (seconds). */
  liberationSeconds: 60,
  /** Attack boost: the liberated player's attacks pay ÷ (1 + this) (+50 % attack; inside the clamped situational product). */
  liberationAttackBonus: 0.5,
  /** Troop growth × (1 + this) during the boost (+50 %). */
  liberationGrowthBonus: 0.5,
  /** At most one Liberation boost per this many seconds (no farming by handing a capital back and forth). */
  liberationCooldownSeconds: 300,
  // --- nation AI (ai/nation/capital.ts) ---------------------------------------------------
  /** A completed own defense post within this distance (tiles) of the capital is its garrison. */
  aiGarrisonRadius: 16,
  /** A defense post is built near the capital when an enemy border or front is within this distance. */
  aiGarrisonThreatRadius: 60,
  /** The capital is under threat when an enemy attack front is within this distance (tiles). */
  aiThreatRadius: 22,
  /** Under threat: the counter-attack goes out with this share of the attacker's troops at least … */
  aiDefendOverkill: 1.3,
  /** … and the desired troop ratio rises by this much. */
  aiThreatTroopRatio: 0.2,
  /** Strike: an enemy capital within this distance (tiles) of our land is a target … */
  aiStrikeRange: 40,
  /** … when our attack force is at least this × the defender's troops … */
  aiStrikeMargin: 1.25,
  /** … with this chance per decision (× aggression). */
  aiStrikeChance: 0.3,
  /** Own border tiles sampled per strike scan. */
  aiStrikeSamples: 96,
  // --- stage J nation AI (ai/nation/capital.ts) ---------------------------------------------
  /** Retake: our captured capital is a target when it lies within this distance (tiles) of our land … */
  aiRetakeRange: 60,
  /** … and our attack force is at least this × the holder's troops (scaled by how much of its land is near). */
  aiRetakeMargin: 1.1,
  /** Guard: a captured capital we hold keeps a defense post within this distance (tiles) once an enemy is near. */
  aiGuardRadius: 12,
  /** Provisional capital: the nation declares one when it has this many times the price in gold (keeps a reserve). */
  aiProvisionalGoldMargin: 1.5,
};

/**
 * "Defeating a tribe or nation pays you." (wave 4 P3, src/core/empire/plunder.ts): a fixed bounty
 * per eliminated player on top of GOLD.eliminationShare, and loot when a city, port, factory or
 * capital is captured. Small on purpose (no snowballing): a captor bigger than its victim gets
 * less (× max(minScale, sqrt(victim tiles / captor tiles))).
 */
export const PLUNDER = {
  /** Kill switch (tests / balance runs). */
  enabled: true,
  /** Loot per level of a captured structure (gold). */
  cityGold: 20_000,
  portGold: 15_000,
  factoryGold: 35_000,
  /** Loot for taking a capital: this flat amount … */
  capitalGold: 100_000,
  /** … plus this share of the victim's gold. */
  capitalGoldShare: 0.15,
  /** Most gold one plunder yields. */
  maxGold: 400_000,
  /** Share of the loot taken out of the victim's treasury (as far as it has gold); the rest is spoils. */
  victimShare: 0.5,
  /** Lowest anti-snowball scale (a captor many times bigger than its victim). */
  minScale: 0.25,
  /** A structure is looted at most once per this many seconds (no farming by handing it back and forth). */
  cooldownSeconds: 180,
  /** A player's capital is looted at most once per this many seconds (stage J: a capital handed back and forth pays once). */
  capitalCooldownSeconds: 180,
  /** Bounty for eliminating a tribe … */
  tribeBounty: 10_000,
  /** … and a nation or human. */
  nationBounty: 60_000,
};

// =============================================================================
// 20. AI DIFFICULTY
// =============================================================================

export interface DifficultySpec {
  /** Growth / gold multipliers for nations. */
  nationGrowthMult: number;
  nationGoldMult: number;
  /** Multiplier on nations' attack cost per tile (< 1 = stronger). */
  nationAttackCostMult: number;
  /** Bots (tribes). */
  botGrowthMult: number;
  botGoldMult: number;
  botAttackCostMult: number;
  /** Ticks between AI decisions (reaction delay). */
  nationReactionTicks: number;
  botReactionTicks: number;
  /** 0..1 general aggression scaler. */
  aggression: number;
  /** Allow nations to use nukes. */
  nationsNuke: boolean;
  /**
   * Ticks after the spawn phase before nations launch unprovoked nukes (only with
   * `nationsNuke`; retaliation against a player that nuked them is never delayed).
   */
  nationNukeDelayTicks: number;
  /**
   * Grace period (ticks after the spawn phase, game.spawnEndTick()) in which nations start no
   * attacks — land or boat — on a human player who has not attacked them first. Retaliation is
   * never delayed.
   * Stage N (final rebalance): Hard 600 -> 3 000 (five minutes) and Impossible 0 -> 600 — the
   * scripted human was gone after 1-3.5 minutes on Hard and 1 minute on Impossible (no fair first
   * five minutes; with 1 800 it still fell right after the grace, at 3-5 minutes).
   */
  nationHumanGraceTicks: number;
  /**
   * Wave 4 stage N (firstgame, ai/breathingRoom.ts): ticks after the spawn phase in which no
   * nation attacks a human at all, not even to hit back (a new player's first minute).
   */
  nationHumanShieldTicks: number;
  /**
   * Naval tactics of nations (0..3): 0 = warships stay on their port patrol and boats are only
   * recalled when a human the grace period protects took their landing (that recall applies at
   * every level); 1 = damaged warships are sent home for repairs and boats whose landing became
   * friendly are recalled; 2 = + ports / trade lanes guarded, enemy transports heading for own
   * coasts hunted, no boats into (and recalls away from) hostile warships; 3 = + own warships
   * clear the landing zone of a boat first (escort) and hunt farther.
   */
  nationNavalTactics: number;
  /**
   * 0..1 team play of nations: donations to allies / teammates in trouble, a shared focus target
   * (target marks, pings), answers to help requests, sharing neutral land with friends.
   */
  nationTeamPlay: number;
  /**
   * Nuke defence of nations (0..2): 0 = SAMs only from the normal build plan; 1 = + SAMs at the
   * most valuable uncovered cluster and a SAM rush for an incoming bomb when a SAM can still be
   * ready before its impact; 2 = + MIRV threat handling
   * (extra / upgraded SAMs, pre-emptive strikes on a MIRV-capable enemy's silos when nations nuke).
   */
  nationNukeDefense: number;
}

export const AI_DIFFICULTY: Record<Difficulty, DifficultySpec> = {
  [Difficulty.Easy]: { nationGrowthMult: 0.65, nationGoldMult: 0.7, nationAttackCostMult: 1.3, botGrowthMult: 0.6, botGoldMult: 0.6, botAttackCostMult: 1.25, nationReactionTicks: 50, botReactionTicks: 60, aggression: 0.25, nationsNuke: false, nationNukeDelayTicks: 6000, nationHumanGraceTicks: 9000, nationHumanShieldTicks: 600, nationNavalTactics: 1, nationTeamPlay: 0.3, nationNukeDefense: 0 },
  [Difficulty.Medium]: { nationGrowthMult: 0.85, nationGoldMult: 0.9, nationAttackCostMult: 1.1, botGrowthMult: 0.75, botGoldMult: 0.75, botAttackCostMult: 1.1, nationReactionTicks: 35, botReactionTicks: 45, aggression: 0.4, nationsNuke: false, nationNukeDelayTicks: 6000, nationHumanGraceTicks: 4200, nationHumanShieldTicks: 600, nationNavalTactics: 2, nationTeamPlay: 0.6, nationNukeDefense: 1 },
  [Difficulty.Hard]: { nationGrowthMult: 1.2, nationGoldMult: 1.2, nationAttackCostMult: 0.9, botGrowthMult: 0.85, botGoldMult: 0.85, botAttackCostMult: 1.0, nationReactionTicks: 15, botReactionTicks: 35, aggression: 0.7, nationsNuke: true, nationNukeDelayTicks: 3000, nationHumanGraceTicks: 3000, nationHumanShieldTicks: 0, nationNavalTactics: 3, nationTeamPlay: 0.85, nationNukeDefense: 2 },
  [Difficulty.Impossible]: { nationGrowthMult: 1.5, nationGoldMult: 1.5, nationAttackCostMult: 0.75, botGrowthMult: 1, botGoldMult: 1, botAttackCostMult: 0.9, nationReactionTicks: 8, botReactionTicks: 25, aggression: 0.9, nationsNuke: true, nationNukeDelayTicks: 1800, nationHumanGraceTicks: 600, nationHumanShieldTicks: 0, nationNavalTactics: 3, nationTeamPlay: 1, nationNukeDefense: 2 },
};

// =============================================================================
// GameConfig
// =============================================================================

export interface ConfigSections {
  spawn: typeof SPAWN;
  population: typeof POPULATION;
  gold: typeof GOLD;
  attack: typeof ATTACK;
  terrain: typeof TERRAIN;
  enclave: typeof ENCLAVE;
  regions: typeof REGIONS;
  naval: typeof NAVAL;
  trade: typeof TRADE;
  warship: typeof WARSHIP;
  structures: typeof STRUCTURES;
  rail: typeof RAIL;
  nukes: typeof NUKES;
  sam: typeof SAM;
  fallout: typeof FALLOUT;
  diplomacy: typeof DIPLOMACY;
  social: typeof SOCIAL;
  // Wave 4 stage C part 2 (module allywar).
  allyWar: typeof ALLYWAR;
  // Wave 4 stage C (module economy).
  climate: typeof CLIMATE;
  spawnBonus: typeof SPAWN_BONUS;
  resources: typeof RESOURCES;
  seasons: typeof SEASONS;
  // Wave 4 P2 (module extractor).
  extractor: typeof EXTRACTOR;
  // Wave 4 P7 + P3 (module capital).
  capital: typeof CAPITAL;
  plunder: typeof PLUNDER;
  // Wave 4 P2 + stage D (3) (module ships).
  ships: typeof SHIPS;
  // Wave 4 P16 (module passage).
  passage: typeof PASSAGE;
  // Wave 4 stage D (3) + P5 (module navycontrol).
  navalWar: typeof NAVALWAR;
  // Wave 4 P13 (module diplomacy2).
  diplomacy2: typeof DIPLOMACY2;
}

/** Per-game overrides (e.g. tests): every section may be partially replaced. */
export type GameConfigOverrides = {
  [K in keyof ConfigSections]?: Partial<ConfigSections[K]>;
} & {
  build?: Partial<Record<string, Partial<BuildSpec>>>;
  difficulty?: Partial<DifficultySpec>;
};

/**
 * Normalise untrusted / partial settings into a safe GameSettings. Stage J (module modes): the
 * ruleset and the game mode are resolved into explicit toggles / victory switches / disabled units
 * (src/core/rules/ruleset.ts resolveSettings; settings made before stage J play as stored).
 */
export function normalizeSettings(s: GameSettings): GameSettings {
  const num = (v: unknown, def: number, lo: number, hi: number): number =>
    typeof v === "number" && Number.isFinite(v) ? clamp(v, lo, hi) : def;
  const difficulty = Object.values(Difficulty).includes(s.difficulty) ? s.difficulty : Difficulty.Medium;
  return resolveSettings({
    ...s,
    teamCount: Math.floor(num(s.teamCount, 2, 2, 8)),
    difficulty,
    bots: Math.floor(num(s.bots, 0, 0, 400)),
    nations: !!s.nations,
    seed: typeof s.seed === "number" && Number.isFinite(s.seed) ? s.seed : 1,
    spawnPhaseSeconds: num(s.spawnPhaseSeconds, 10, 0, SPAWN.maxSpawnPhaseSeconds),
    winTerritoryPercent: num(s.winTerritoryPercent, 80, WIN.minPercent, WIN.maxPercent),
    timeLimitMinutes: num(s.timeLimitMinutes, 0, 0, 24 * 60),
    infiniteGold: !!s.infiniteGold,
    infiniteTroops: !!s.infiniteTroops,
    instantBuild: !!s.instantBuild,
    disableNukes: !!s.disableNukes,
    disabledUnits: Array.isArray(s.disabledUnits) ? s.disabledUnits.slice() : [],
    gameSpeed: num(s.gameSpeed, 1, 0.25, 4),
  });
}

export class GameConfig implements ConfigSections {
  readonly settings: GameSettings;
  readonly spawn: typeof SPAWN;
  readonly population: typeof POPULATION;
  readonly gold: typeof GOLD;
  readonly attack: typeof ATTACK;
  readonly terrain: typeof TERRAIN;
  readonly enclave: typeof ENCLAVE;
  readonly regions: typeof REGIONS;
  readonly naval: typeof NAVAL;
  readonly trade: typeof TRADE;
  readonly warship: typeof WARSHIP;
  readonly structures: typeof STRUCTURES;
  readonly rail: typeof RAIL;
  readonly nukes: typeof NUKES;
  readonly sam: typeof SAM;
  readonly fallout: typeof FALLOUT;
  readonly diplomacy: typeof DIPLOMACY;
  readonly social: typeof SOCIAL;
  readonly allyWar: typeof ALLYWAR;
  readonly climate: typeof CLIMATE;
  readonly spawnBonus: typeof SPAWN_BONUS;
  readonly resources: typeof RESOURCES;
  readonly seasons: typeof SEASONS;
  readonly extractor: typeof EXTRACTOR;
  readonly capital: typeof CAPITAL;
  readonly plunder: typeof PLUNDER;
  readonly ships: typeof SHIPS;
  readonly passage: typeof PASSAGE;
  readonly navalWar: typeof NAVALWAR;
  readonly diplomacy2: typeof DIPLOMACY2;
  readonly build: Record<string, BuildSpec>;
  readonly difficulty: DifficultySpec;

  constructor(settings: GameSettings, overrides: GameConfigOverrides = {}) {
    this.settings = settings;
    this.spawn = { ...SPAWN, ...overrides.spawn };
    this.population = { ...POPULATION, ...overrides.population };
    this.gold = { ...GOLD, ...overrides.gold };
    this.attack = { ...ATTACK, ...overrides.attack };
    this.terrain = { ...TERRAIN, ...overrides.terrain };
    this.enclave = { ...ENCLAVE, ...overrides.enclave };
    this.regions = { ...REGIONS, ...overrides.regions };
    this.naval = { ...NAVAL, ...overrides.naval };
    this.trade = { ...TRADE, ...overrides.trade };
    this.warship = { ...WARSHIP, ...overrides.warship };
    this.structures = { ...STRUCTURES, ...overrides.structures };
    this.rail = { ...RAIL, ...overrides.rail };
    this.nukes = { ...NUKES, ...overrides.nukes };
    this.sam = { ...SAM, ...overrides.sam };
    this.fallout = { ...FALLOUT, ...overrides.fallout };
    this.diplomacy = { ...DIPLOMACY, ...overrides.diplomacy };
    this.social = { ...SOCIAL, ...overrides.social };
    this.allyWar = { ...ALLYWAR, ...overrides.allyWar };
    this.climate = { ...CLIMATE, ...overrides.climate };
    this.spawnBonus = { ...SPAWN_BONUS, ...overrides.spawnBonus };
    this.resources = { ...RESOURCES, ...overrides.resources };
    this.seasons = { ...SEASONS, ...overrides.seasons };
    this.extractor = { ...EXTRACTOR, ...overrides.extractor };
    this.capital = { ...CAPITAL, ...overrides.capital };
    this.plunder = { ...PLUNDER, ...overrides.plunder };
    this.ships = { ...SHIPS, ...overrides.ships };
    this.passage = { ...PASSAGE, ...overrides.passage };
    this.navalWar = { ...NAVALWAR, ...overrides.navalWar };
    this.diplomacy2 = { ...DIPLOMACY2, ...overrides.diplomacy2 };
    const build: Record<string, BuildSpec> = {};
    for (const key of Object.keys(BUILD)) {
      const o = overrides.build?.[key];
      build[key] = o ? { ...BUILD[key], ...o, cost: { ...BUILD[key].cost, ...o.cost } } : BUILD[key];
    }
    this.build = build;
    this.difficulty = { ...AI_DIFFICULTY[settings.difficulty] ?? AI_DIFFICULTY[Difficulty.Medium], ...overrides.difficulty };
  }

  // ---------------------------------------------------------------------------
  // Timing / phases
  // ---------------------------------------------------------------------------

  spawnPhaseTicks(): number {
    return secondsToTicks(clamp(this.settings.spawnPhaseSeconds, 0, this.spawn.maxSpawnPhaseSeconds));
  }

  /** Tick at which the time limit ends the game, or -1 when there is none. */
  timeLimitTick(): number {
    if (!(this.settings.timeLimitMinutes > 0)) return -1;
    return this.spawnPhaseTicks() + secondsToTicks(this.settings.timeLimitMinutes * 60);
  }

  winTerritoryFraction(): number {
    return clamp(this.settings.winTerritoryPercent, WIN.minPercent, WIN.maxPercent) / 100;
  }

  // ---------------------------------------------------------------------------
  // Settings helpers
  // ---------------------------------------------------------------------------

  /** Infinite gold/troops settings apply to humans only (sandbox options). */
  hasInfiniteGold(type: PlayerType): boolean {
    return this.settings.infiniteGold && type === PlayerType.Human;
  }

  hasInfiniteTroops(type: PlayerType): boolean {
    return this.settings.infiniteTroops && type === PlayerType.Human;
  }

  isUnitDisabled(type: UnitType): boolean {
    if (this.settings.disabledUnits.includes(type)) return true;
    // Wave 4 P2: extractors only boost resources.
    if (type === UnitType.Extractor && !featureOn(this.settings, "resources")) return true;
    // Wave 4 stage E (airforce): airfields belong to the attack types.
    if (type === UnitType.Airfield && (!AIRFORCE.enabled || !featureOn(this.settings, "attackTypes"))) return true;
    if (this.settings.disableNukes) {
      if (
        type === UnitType.AtomBomb ||
        type === UnitType.HydrogenBomb ||
        type === UnitType.MIRV ||
        type === UnitType.MIRVWarhead ||
        type === UnitType.MissileSilo
      ) {
        return true;
      }
    }
    return false;
  }

  // ---------------------------------------------------------------------------
  // Population & economy
  // ---------------------------------------------------------------------------

  growthMult(type: PlayerType): number {
    if (type === PlayerType.Nation) return this.difficulty.nationGrowthMult;
    if (type === PlayerType.Bot) return this.difficulty.botGrowthMult;
    return 1;
  }

  goldMult(type: PlayerType): number {
    if (type === PlayerType.Nation) return this.difficulty.nationGoldMult;
    if (type === PlayerType.Bot) return this.difficulty.botGoldMult;
    return 1;
  }

  /** Number of tribes for a map: settings.bots is the cap, the land sets the density. */
  tribeCount(landTiles: number): number {
    const per = Math.max(1, this.spawn.tribeLandPerTribe);
    return Math.max(0, Math.min(this.settings.bots, Math.floor(landTiles / per)));
  }

  startPopulation(type: PlayerType, strength = 1): number {
    const base = this.spawn.startPopulation;
    if (type === PlayerType.Bot) return base * this.spawn.botStartPopulationMult;
    if (type === PlayerType.Nation) {
      const s = clamp(Math.floor(strength), 1, 3);
      return base * (1 + this.spawn.nationStrengthBonus * (s - 1));
    }
    return base;
  }

  maxPopulation(tiles: number, cityLevels: number): number {
    const p = this.population;
    return Math.floor(p.baseMax + p.perTileK * pow075(tiles) + p.cityPerLevel * cityLevels);
  }

  /** Population growth for one tick (>= 0), before clamping to the headroom. */
  populationGrowth(pop: number, maxPop: number, type: PlayerType): number {
    if (!(maxPop > 0) || pop >= maxPop) return 0;
    const p = this.population;
    const raw = (p.baseGrowth + pow075(pop) / p.growthDivisor) * (1 - pop / maxPop);
    return raw * this.settings.gameSpeed * this.growthMult(type);
  }

  /** Gold produced by workers for one tick. */
  goldFromWorkers(workers: number, type: PlayerType): number {
    if (!(workers > 0)) return 0;
    return this.gold.perSqrtWorker * Math.sqrt(workers) * this.settings.gameSpeed * this.goldMult(type);
  }

  clampTroopRatio(r: number): number {
    return clamp(r, this.population.minTroopRatio, this.population.maxTroopRatio);
  }

  // ---------------------------------------------------------------------------
  // Attacks
  // ---------------------------------------------------------------------------

  terrainCostMult(t: TerrainType): number {
    switch (t) {
      case TerrainType.Highland:
        return this.terrain.highlandCost;
      case TerrainType.Mountain:
        return this.terrain.mountainCost;
      default:
        return this.terrain.plainsCost;
    }
  }

  terrainDelay(t: TerrainType): number {
    switch (t) {
      case TerrainType.Highland:
        return this.terrain.highlandDelay;
      case TerrainType.Mountain:
        return this.terrain.mountainDelay;
      default:
        return this.terrain.plainsDelay;
    }
  }

  /** Multiplier on attack cost for the attacker (AI difficulty). */
  attackCostMult(attackerType: PlayerType): number {
    if (attackerType === PlayerType.Nation) return this.difficulty.nationAttackCostMult;
    if (attackerType === PlayerType.Bot) return this.difficulty.botAttackCostMult;
    return 1;
  }

  /**
   * Base troop cost of one plains tile of unowned land for an attacker owning `attackerTiles` on a
   * map of `mapLandTiles` land tiles (the size part follows the attacker's share of the map).
   */
  neutralTileBaseCost(attackerTiles: number, mapLandTiles: number): number {
    const a = this.attack;
    const share = (attackerTiles > 0 ? attackerTiles : 0) / Math.max(1, a.neutralShareMinLand, mapLandTiles);
    return a.neutralBase + a.neutralShareK * Math.sqrt(share);
  }

  /**
   * Base troop cost of one plains tile of an enemy with the given troop density
   * (home troops / tiles). Never cheaper than unowned land for the same attacker.
   */
  enemyTileBaseCost(attackerTiles: number, defenderDensity: number, mapLandTiles: number): number {
    const neutral = this.neutralTileBaseCost(attackerTiles, mapLandTiles);
    const a = this.attack;
    const density = defenderDensity > 0 ? defenderDensity : 0;
    return Math.max(neutral, neutral * a.enemyNeutralShare + a.enemyBase + density * a.densityK);
  }

  // ---------------------------------------------------------------------------
  // Building
  // ---------------------------------------------------------------------------

  buildSpec(type: UnitType): BuildSpec | undefined {
    return this.build[type];
  }

  /**
   * Gold cost of the next unit of `type` for a player that already owns `ownedCount`
   * units/levels of that type (structures: levels summed incl. under construction;
   * warships: count; nukes: flat).
   */
  unitCost(type: UnitType, ownedCount: number): number {
    const spec = this.build[type];
    if (!spec) return 0;
    const c = spec.cost;
    return Math.min(c.max, c.base + c.increment * Math.max(0, ownedCount));
  }

  constructionTicks(type: UnitType): number {
    if (this.settings.instantBuild) return 0;
    const spec = this.build[type];
    return spec ? secondsToTicks(spec.constructionSeconds) : 0;
  }

  upgradeTicks(type: UnitType): number {
    return Math.round(this.constructionTicks(type) * this.structures.upgradeTimeFraction);
  }

  // ---------------------------------------------------------------------------
  // Nukes / SAM / silo
  // ---------------------------------------------------------------------------

  nukeSpec(type: UnitType): NukeSpec | undefined {
    if (type === UnitType.AtomBomb || type === UnitType.HydrogenBomb || type === UnitType.MIRVWarhead) {
      return this.nukes[type];
    }
    return undefined;
  }

  nukeFlightTicks(distanceTiles: number): number {
    const n = this.nukes;
    return clamp(Math.ceil(distanceTiles / n.flightSpeed), n.minFlightTicks, n.maxFlightTicks);
  }

  siloReloadTicks(): number {
    return secondsToTicks(this.structures.siloReloadSeconds);
  }

  samCooldownTicks(): number {
    return secondsToTicks(this.sam.cooldownSeconds);
  }
}

// =============================================================================
// 19f. UNREST AND REBELLIONS (wave 4 P10, module unrest; src/core/empire/unrest.ts,
//      docs/GAME.md#unrest-and-rebellions). Unrest is 0..100 per chunk of land (its main owner).
// =============================================================================

/** "Huge empires get restless far from their capital." (settings.unrest) */
export const UNREST = {
  /** Kill switch (tests of other systems, A/B perf runs); the game setting `unrest` turns it off per game. */
  enabled: true,
  /** Chunk edge in tiles: unrest is tracked per chunk for the player owning most of its land. */
  chunkSize: 16,
  /** Ticks for one sweep over every chunk (time-sliced: each tick updates numChunks / sweepTicks chunks). */
  sweepTicks: 50,
  /**
   * Empires with fewer tiles than max(minTiles, minShare × the map's land) never get unrest (and
   * their chunks calm down).
   */
  minTiles: 4_000,
  minShare: 0.025,
  /**
   * The empire's size factor (distance unrest, cut-off unrest) grows linearly from the minimum
   * above to max(fullTiles, fullShare × the map's land) tiles.
   */
  fullTiles: 30_000,
  fullShare: 0.12,
  /** No distance unrest within this many tiles of the capital. */
  safeRadius: 80,
  /** Target unrest per tile of distance beyond safeRadius (× the size factor). */
  perTile: 0.4,
  /**
   * Target unrest of land cut off from the capital (× the cut-off share of the owner's tiles in the
   * chunk) × (cutOffSizeBase + (1 − cutOffSizeBase) × the size factor): mid-sized empires' cut-off
   * land only gets restless, a huge empire's rebels.
   */
  cutOffTarget: 130,
  cutOffSizeBase: 0.5,
  /** Unrest moves toward its target by this much per second … */
  riseRate: 1,
  /** … this many times faster in cut-off land … */
  cutOffRiseMult: 4,
  /** … and falls by this much per second when the target is lower. */
  fallRate: 2,
  /** Instant unrest bump for land just taken from another player (× the conquered share of the chunk). */
  conquestBump: 30,
  /** Own completed cities and defense posts calm chunks whose centre lies within this many tiles. */
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 28. */
  calmRadius: 36,
  /** Target unrest removed per city level in range. */
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 12. */
  cityCalm: 20,
  /** Target unrest removed per defense post level in range. */
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 20. */
  postCalm: 30,
  /** Most target unrest that structures remove from one chunk. */
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 80. */
  calmMax: 100,
  /** Chunks at or above this unrest count as restless (overlay, HUD). */
  restlessAt: 25,
  /** The HUD warns from this unrest on (rebellion risk). */
  warnAt: 60,
  /** A chunk reaching this unrest rebels (when the empire's cooldown allows it). */
  rebelAt: 100,
  /** Neighbouring chunks of the same owner at or above this unrest join the rebellion. */
  spreadAt: 70,
  /** A rebellion needs at least this many connected tiles; smaller pieces calm down instead. */
  minRebelTiles: 30,
  /** Ticks between two rebellions in one empire (2.5 min). */
  cooldownTicks: 1_500,
  /** Most rebellions in the whole world per tick. */
  maxRebellionsPerTick: 1,
  /** Share of the owner's troops on the rebelling land that defect to the rebels. */
  garrisonShare: 1,
  /** Extra rebel troops per rebelling tile (capped at the rebels' max population). */
  levyPerTile: 10,
  /**
   * Fresh rebels cannot be annexed by the enclosed-small-player rule (combat/regions.ts) for this
   * many ticks (5 min): rebels rising inside an empire touch only their former owner, who has to
   * put them down by force meanwhile. Afterwards the normal rules apply again.
   */
  rebelGraceTicks: 3_000,
  /** Restless land grows this much slower at unrest 100 (linear; weighted by the restless land share). */
  growthPenalty: 0.5,
  /** Ticks between two overlay snapshots (TickUpdate.unrest). */
  syncTicks: 10,
  // --- AI (ai/nation/mercenaries.ts) -------------------------------------------------------
  /** Nations build a defense post near their most restless chunk from this unrest on. */
  aiCalmAt: 55,
  /** … at most once per this many decisions. */
  aiCalmEvery: 6,
};

// =============================================================================
// 19g. MERCENARIES (wave 4 P11, module unrest; src/core/empire/mercenaries.ts)
// =============================================================================

/** "Turn gold into troops when you need them now." (settings.mercenaries) */
export const MERCENARIES = {
  /** Kill switch; the game setting `mercenaries` turns them off per game. */
  enabled: true,
  /** Troops hired per purchase: this share of the max population (capped by the free population room). */
  troopShare: 0.15,
  /** A purchase needs room for at least this many troops. */
  minTroops: 100,
  /** Fixed part of the price (gold). */
  basePrice: 50_000,
  /** Gold per hired troop. */
  goldPerTroop: 5,
  /** Every recent purchase multiplies the price by this ("heat"). */
  escalation: 1.5,
  /** Most heat steps (price cap = escalation^maxHeat × the base price). */
  maxHeat: 6,
  /** One heat step cools down after this many ticks without a purchase (60 s). */
  coolTicks: 600,
  /** Ticks between two purchases (10 s). */
  lockTicks: 100,
  // --- AI (ai/nation/mercenaries.ts) -------------------------------------------------------
  /** Under heavy attack: incoming troops at least this share of the home troops. */
  aiThreatRatio: 0.6,
  /** Most of its gold a nation spends on one purchase under heavy attack … */
  aiDefenseGoldShare: 0.45,
  /** … and before a decisive offensive. */
  aiOffenseGoldShare: 0.25,
  /** Offensive: the target's troops are at least this share of ours (a close fight). */
  aiOffenseCloseRatio: 0.7,
  /** Gold a nation always keeps after a purchase. */
  aiReserveGold: 60_000,
};

// =============================================================================
// 19h. SHIPS (wave 4 P2 + stage D (3), module ships; docs/GAME.md#automatic-navy-the-naval-stance): patrol boats,
//      battleships, the shipyard, coastal bombardment and cargo ships. Combat stats per ship
//      type (health, speed, guns, armour) live in executions/naval/shipStats.ts.
// =============================================================================

/** "Each ship has one clear job." */
export const SHIPS = {
  // --- Limits / shipyard -------------------------------------------------------------------
  /** Most patrol boats per player. */
  maxPatrolBoats: 20,
  /** Most battleships per player. */
  maxBattleships: 8,
  /** A shipyard must stand within this many tiles (Euclidean) of an own completed port. */
  shipyardPortRadius: 16,

  // --- Combat stats (rows of naval/shipStats.ts, registered by naval/patrol.ts and naval/battleship.ts;
  //     the warship for comparison: 1000 health, 250 damage every 20 ticks, range 30, speed 1) ------
  /** Patrol boat: cheap, fast, weak; chases only transports and trade ships (and captures trade ships). */
  patrolHealth: 400,
  patrolDamage: 120,
  patrolCooldownTicks: 12,
  patrolRange: 22,
  /** Hostile transports / trade ships within this distance of a patrolling patrol boat are chased. */
  patrolTargetRange: 70,
  patrolSpeed: 1.8,
  patrolArmor: 0,
  /** Weight in blockades / strait control / sea superiority (a warship = 1). */
  patrolPresence: 0.4,
  patrolRetreatAt: 0.45,
  patrolHealPerTick: 2,
  /** Battleship: expensive, slow, heavily armoured; never chases, fires at ships in range and shells coasts. */
  battleshipHealth: 3_500,
  battleshipDamage: 400,
  battleshipCooldownTicks: 30,
  battleshipRange: 36,
  battleshipSpeed: 0.6,
  /** Share of incoming shell damage the armour absorbs (clamped to NAVALWAR.maxArmor). */
  battleshipArmor: 0.45,
  battleshipPresence: 2.5,
  battleshipRetreatAt: 0.25,
  battleshipHealPerTick: 5,

  // --- Coastal bombardment (settings.bombardment) ------------------------------------------
  /**
   * Battleships shell enemy land within this many tiles of the ship (so within this distance of
   * the coast) once per `battleshipBombardCooldownTicks`; warships do the same, weaker.
   */
  battleshipBombardRange: 14,
  battleshipBombardCooldownTicks: 40,
  /** Radius (tiles) of the blast of one battleship shell (troops die on every enemy tile in it). */
  battleshipBlastRadius: 2,
  /** Structure damage of one battleship shell (structures have `structureHealth`). */
  battleshipStructureDamage: 300,
  warshipBombardRange: 8,
  warshipBombardCooldownTicks: 80,
  warshipBlastRadius: 1,
  warshipStructureDamage: 100,
  /** Tiles per tick a bombardment shell flies (visual flight; it lands after distance / speed ticks). */
  shellSpeed: 4,
  /**
   * Troops killed per hit = victim troops per tile × enemy tiles in the blast × this (shells land
   * where the coastal garrison stands), at most `maxKillShare` of the victim's troops per hit.
   */
  troopKillMult: 4,
  maxKillShare: 0.01,
  /**
   * Health of a structure under fire. A structure whose health reaches 0 loses one level (level 1:
   * destroyed) and is repaired to full; damaged structures repair this much per tick.
   */
  structureHealth: 1_000,
  structureRepairPerTick: 1,
  /** A ship that is moving to an ordered spot still bombards on its way. Ships only shell players
   * they are at war with (an active attack either way). Most shells in flight per game: */
  maxShellsInFlight: 256,
  /** Victims get at most one "being shelled" log line per this many ticks (per victim). */
  logCooldownTicks: 300,

  // --- Cargo ships (settings.resources) ----------------------------------------------------
  /**
   * A trade ship carries the sender's deposit type with the largest level surplus over the
   * receiver (ties → lower ResourceType); none when the sender has no surplus.
   * On arrival the receiver's level of that resource gets + min(cargoMaxBonus, cargoShare ×
   * surplus) for cargoTicks (the best active cargo bonus per resource counts, not the sum).
   * The extra gold of complementary trade is the economy's trade multiplier (not counted twice).
   */
  cargoShare: 0.25,
  cargoMaxBonus: 0.12,
  cargoTicks: 1_800,

  // --- AI (ai/nation/fleet2.ts) ------------------------------------------------------------
  /**
   * A nation at war with a coastal enemy commits to a shipyard (and then saves up for it and its
   * first battleship) once its gold plus this many ticks of worker income (`goldPerTick`, × the
   * personality's navy weight) covers the shipyard and the battleship price. A second
   * battleship needs twice its price within the same horizon.
   */
  aiSaveTicks: 1_200,
};

// =============================================================================
// 19w. WORLD EVENTS (wave 4 P12, module worldevents; src/core/world/events.ts,
//      docs/GAME.md#world-events). Times in seconds at game speed 1 (a faster game speed
//      shortens intervals and durations, like the seasons); the warning is real seconds.
// =============================================================================

/** "The world itself throws surprises at everybody." (settings.worldEvents: off / rare / normal / chaos) */
export const WORLDEVENTS = {
  /** Kill switch (tests of other systems); the game setting `worldEvents: "off"` turns them off per game. */
  enabled: true,
  /** Every event is announced this long before it starts (seconds, not scaled by the game speed). */
  warnSeconds: 30,
  /** First announcement after the spawn phase per frequency. */
  firstDelaySeconds: { rare: 360, normal: 210, chaos: 75 },
  /** Mean time between two announcements per frequency. */
  intervalSeconds: { rare: 420, normal: 240, chaos: 100 },
  /** Random spread of the interval (± this share). */
  intervalJitter: 0.3,
  /** Most events announced or running at the same time per frequency. */
  maxConcurrent: { rare: 1, normal: 2, chaos: 4 },
  /** No free slot or no valid place: try again after this many seconds. */
  retrySeconds: 15,
  /** Relative chance of each kind (among the kinds this map and season allow). */
  weights: { plague: 3, goldRush: 3, storm: 3, flood: 2, volcano: 2 },
  /** The previous event's kind is this much less likely to come again right away. */
  repeatKindMult: 0.35,
  /** A new event keeps placeSpacing × √(map tiles) away from the centres of the last placeMemory events. */
  placeMemory: 6,
  placeSpacing: 0.12,
  /**
   * Fairness: a harmful event (plague, storm, flood, volcano) skips places whose main owner was the
   * main victim of one of the last victimMemory harmful events (when another place is possible).
   */
  victimMemory: 2,
  /** Candidate places examined per announcement. */
  candidates: 24,
  /** All events together never lower a player's growth below this multiplier. */
  minGrowthMult: 0.7,
  /** Edge (tiles) of the chunks of the precomputed candidate grid. */
  gridChunk: 16,
  /** Durations are scaled by this random spread (± share). */
  durationJitter: 0.2,

  /** "A plague slows the growth of every people living in the marked region." */
  plague: {
    durationSeconds: 100,
    /** Region radius = radiusShare × √(map tiles), clamped to [minRadius, maxRadius]. */
    radiusShare: 0.03,
    minRadius: 10,
    maxRadius: 60,
    /** The region is the circle of chunks of this edge (tiles) around the centre. */
    chunk: 8,
    /** Growth × (1 − growthPenalty × the share of the player's land inside the region). */
    growthPenalty: 0.6,
    /** Only inhabited places: at least this share of the candidate chunk's land is owned. */
    minOwnedShare: 0.25,
  },
  /** "A gold rush makes one deposit cluster yield far more for a while." */
  goldRush: {
    durationSeconds: 120,
    radiusShare: 0.012,
    minRadius: 6,
    maxRadius: 24,
    /** Smallest cluster: deposit tiles of the rush's type inside the region. */
    minTiles: 6,
    /** Every own cluster tile counts this many times for the deposit bonus level (1 = no boost). */
    depositWeight: 3,
    /** Gold per own cluster tile every payTicks (× game speed). */
    goldPerTile: 40,
    payTicks: 50,
    /** Most gold one player gets per payout. */
    maxGoldPerPay: 6000,
  },
  /** "A storm at sea slows every ship in the marked waters." */
  storm: {
    durationSeconds: 90,
    radiusShare: 0.035,
    minRadius: 12,
    maxRadius: 70,
    /** Ships inside the storm move at this share of their speed (boats arrive late). */
    speedMult: 0.4,
    /** Candidate chunks: at least this share of ocean tiles. */
    minOceanShare: 0.6,
  },
  /** "Spring floods turn rivers and their banks into mud that slows every attack." */
  flood: {
    durationSeconds: 90,
    radiusShare: 0.03,
    minRadius: 10,
    maxRadius: 60,
    /** Land within this many tiles of a flooded river tile floods too (its banks). */
    bankWidth: 2,
    /** Attack fronts on flooded tiles: front speed weight × (1 + slow). */
    slow: 1.2,
    /** Smallest flood: river tiles inside the region. */
    minRiverTiles: 8,
    /** With seasons on, floods start in spring only, with at least this share of spring left. */
    minSpringLeft: 0.25,
  },
  /** "A volcano erupts: it destroys nearby buildings and its ash slows growth." */
  volcano: {
    durationSeconds: 100,
    /** Structures within the blast radius (blastShare × √(map tiles), clamped) are destroyed at the eruption. */
    blastShare: 0.004,
    minBlast: 3,
    maxBlast: 8,
    /** Ash region (growth drop) radius. */
    radiusShare: 0.012,
    minRadius: 6,
    maxRadius: 22,
    /** Growth × (1 − growthPenalty × the share of the player's land under the ash). */
    growthPenalty: 0.7,
    /** A volcano is a mountain peak of at least this elevation (terrain magnitude). */
    minPeak: 20,
  },

  // --- AI (ai/nation/events.ts) -------------------------------------------------------------
  /** Nations want this much more strength before attacking a neighbour across a flood. */
  aiFloodMargin: 1.6,
  /** …and before attacking a neighbour whose land lies largely in a plague region. */
  aiPlagueMargin: 1.4,
  /** "Largely": at least this share of the target's land. */
  aiPlagueShare: 0.25,
  /** Nations reach for a gold rush whose centre lies within this many radii of their border. */
  aiGoldRushReach: 4,
  /** Target score bonus for a neighbour holding a gold rush (taking it pays). */
  aiGoldRushScore: 0.8,
};

// =============================================================================
// 19h. AIRFORCE (wave 4 stage E, module airforce; src/core/executions/air)
// =============================================================================

/**
 * "Airfields let you strike from the sky and drop troops behind the lines." (settings.attackTypes)
 * An airfield has one sortie slot per level; a strike or a drop uses one slot, which reloads in
 * cooldownSeconds. Planes fly straight at planeSpeed; hostile SAMs near their route can shoot
 * them down (each SAM shot uses one of the SAM's interception slots, shared with nukes).
 */
export const AIRFORCE = {
  /** Kill switch; the game setting `attackTypes` turns air power off per game. */
  enabled: true,
  // --- Airfield ----------------------------------------------------------------------------
  /** Reach of a level-1 airfield (Euclidean tiles from the airfield to the target) … */
  range: 160,
  /** … plus this per level above 1. */
  rangePerLevel: 20,
  /** Reload time of one sortie slot after a strike or a drop. */
  cooldownSeconds: 40,
  /** Plane ground speed (tiles per tick). */
  planeSpeed: 2.5,
  /** Shortest / longest flight (ticks). */
  minFlightTicks: 8,
  maxFlightTicks: 200,
  // --- Air strike ---------------------------------------------------------------------------
  /** Gold per strike. */
  strikeGold: 150_000,
  /** Radius (tiles) of the bombed area around the target tile. */
  strikeRadius: 5,
  /** Share of the troops standing on the bombed land that die. */
  killShare: 0.6,
  /** Troops gather at borders: a border tile holds this many times the troops of an interior tile. */
  frontWeight: 4,
  /** One strike kills at most this share of a victim's home troops. */
  maxKillShare: 0.12,
  /**
   * Attacks of hostile players whose front runs through the bombed area lose this share of their
   * troops, scaled by the part of their front inside the area (a narrow front fully inside loses it all).
   */
  attackKillShare: 0.45,
  /** Structures in the area lose this many levels; one at or below it is destroyed (not capitals). */
  structureLevels: 1,
  /** Relation change of a bombed nation toward the striker. */
  relationPenalty: -15,
  // --- Special forces ------------------------------------------------------------------------
  /** Gold per drop. */
  dropGold: 100_000,
  /** The drop zone must lie within this distance (tiles) of the dropper's own land. */
  dropRange: 60,
  /** A drop carries at most this share of the home troops (level-1 airfield) … */
  dropShare: 0.08,
  /** … plus this per airfield level above 1. */
  dropSharePerLevel: 0.02,
  /** Fewest troops a drop may carry. */
  dropMinTroops: 100,
  /** Beachhead penalty scale of the landing attack (1 = like a naval landing). */
  dropPenaltyScale: 1.2,
  // --- Interception (SAM.range, SAM.missileSpeed, SAM.cooldownSeconds) -------------------------
  /** Chance that one SAM missile downs a plane (planes may evade). */
  samHitChance: 0.7,
  // --- AI (ai/nation/airforce.ts) --------------------------------------------------------------
  /**
   * A nation builds its first airfield once it holds this much gold … (stage F balance: 2.5 M was
   * reached by ~13 % of the nations, so air power hardly appeared; rebalance after stage I:
   * 1.5 M → 1 M = the airfield + the reserve + a sortie, so more nations at war fly strikes and
   * get a launch site for the stage I bombs.)
   */
  aiRichGold: 1_000_000,
  /** … and is at war (attacking or attacked by a nation / human, or bordering an enemy it hates). */
  aiMaxAirfields: 2,
  /** Gold a nation keeps after paying for a sortie. */
  aiReserveGold: 300_000,
  /** A strike must be expected to kill at least this many troops per 100 000 gold. */
  aiMinKillsPer100k: 4_000,
  /** A drop needs at least this ratio of drop troops to the defenders' troops per tile × 30. */
  aiDropEdge: 1.5,
  /** Decisions between two sorties of one nation (per difficulty below hard: × 2). */
  aiSortieEvery: 3,
  /** Candidate tiles scored per sortie decision. */
  aiCandidates: 48,
};

// =============================================================================
// 19w. WONDERS AND RUINS (wave 4 P8, module wonders; src/core/world/wonders.ts,
//      docs/GAME.md#wonders)
// =============================================================================

/** Bonuses of one wonder kind (fractions; 0 = none). */
export interface WonderBonus {
  /** Population growth +x. */
  growth: number;
  /** Gold from workers +x. */
  gold: number;
  /** Building speed +x (construction / upgrade time ÷ (1 + x)). */
  build: number;
  /** Defence × (1 + x) on the holder's land within WONDERS.defenseRadius of the wonder. */
  defense: number;
  /** Fog of war: the holder sees this many tiles around the wonder (0 = only the normal sight). */
  vision: number;
}

/** "Hold a wonder for a big bonus — everybody wants it." (settings.wonders) */
export const WONDERS = {
  /** Kill switch (tests of other systems); the game setting `wonders` turns them off per game. */
  enabled: true,
  /** Land tiles per wonder: count = clamp(round(land / landPerWonder), minCount, maxCount). */
  landPerWonder: 60_000,
  minCount: 3,
  maxCount: 8,
  /** Maps with less land get no wonders at all (tiny test maps). */
  minLand: 600,
  /** Wonder centres keep this far from the map edge (tiles). */
  edgeMargin: 4,
  /** Candidate tiles sampled per wonder; the one farthest from the others wins. */
  samples: 64,
  /** Wonders aim for this share of the ideal spacing sqrt(land / count) between each other. */
  spacingShare: 0.55,
  /** Wonders keep at least this far from the map's nation home tiles (no free wonder at spawn). */
  nationClearance: 14,
  /**
   * Stage I warfare [2]: wonders are placed when the spawn phase ends, preferring sites at least
   * this share of the ideal spacing sqrt(land / count) from every human and nation spawn (as far as
   * the samples allow; nationClearance stays the hard minimum).
   */
  spawnClearanceShare: 0.3,
  /** Footprint radius in tiles: the cursor within it counts as over the landmark (tooltip); the centre tile alone decides the holder. */
  footprint: 2,
  /** Ticks between full client snapshots (also sent on every change). */
  syncTicks: 10,
  /** Radius of the defence bonus around a held wonder (tiles). Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 10. */
  defenseRadius: 16,
  /** Caps on the summed bonuses of every wonder a player holds. */
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 0.35 / 0.35 / 0.6. */
  maxGrowth: 0.6,
  maxGold: 0.6,
  maxBuild: 0.8,
  /** Strongest defence multiplier from wonders (the combat caps of ATTACK apply on top). */
  /** Stage I warfare [4] boost pass (docs/BALANCE.md#terrain-and-situational-modifiers): was 0.7. */
  maxDefense: 1,
  /** Bonuses per kind (every wonder also defends its surroundings). */
  bonus: {
    // Stage I warfare [4] boost pass: every value raised (old -> new in docs/BALANCE.md#terrain-and-situational-modifiers).
    ruins: { growth: 0, gold: 0.3, build: 0, defense: 0.5, vision: 0 },
    monument: { growth: 0, gold: 0.1, build: 0.6, defense: 0.5, vision: 0 },
    harbour: { growth: 0.1, gold: 0.25, build: 0, defense: 0.5, vision: 64 },
    sacredMountain: { growth: 0, gold: 0, build: 0, defense: 1, vision: 80 },
    oasis: { growth: 0.35, gold: 0, build: 0, defense: 0.5, vision: 0 },
    worldTree: { growth: 0.25, gold: 0.08, build: 0, defense: 0.6, vision: 0 },
  } as Record<string, WonderBonus>,
  /** Number of invented names per kind (i18n `wonders.name.<kind>.<n>`). */
  namesPerKind: 4,
  // --- AI (ai/nation/wonders.ts) -----------------------------------------------------------
  /** A wonder / the hill within this many tiles of our border is worth going for. */
  aiRange: 45,
  /** Border tiles sampled for the distance to a wonder. */
  aiSamples: 40,
  /** Chance per decision (× aggression × difficulty) to go for a wonder / the hill. */
  aiChance: 0.3,
  /** ... raised this many times while a matching victory condition is on. */
  aiVictoryBoost: 2,
  /** Free attack force at least this many times the holder's troops (×0.5 against tribes). */
  aiMargin: 1.2,
  /** An enemy front within this many tiles of a held wonder is answered (counter-attack). */
  aiDefendRadius: 14,
  /** Counter-attack strength against an attack on our wonder (× its troops). */
  aiDefendOverkill: 1.3,
};

// =============================================================================
// 19v. VICTORY CONDITIONS (wave 4 P9, module wonders; src/core/world/victory.ts)
// =============================================================================

/** "Win by land, wonders, the centre or gold." (settings.wonderVictory / hillVictory / economicVictory) */
export const VICTORY = {
  /** Hill zone radius = clamp(round(sqrt(land) × hillRadiusK), hillMinRadius, hillMaxRadius). */
  hillRadiusK: 0.028,
  hillMinRadius: 5,
  hillMaxRadius: 26,
  /** The zone's centre: the land tile nearest the map centre within this many tiles … */
  hillSearchRadius: 400,
  /** … on a land body at least this many times the zone's area (no tiny islet in the middle). */
  hillMinBodyShare: 3,
  /** A player (team) holds the hill with more than this share of the zone's land. */
  hillHoldShare: 0.5,
  /** Ticks between two hill counts (the zone is small; counted in full). */
  hillSampleTicks: 5,
  /** While nobody holds the hill, the last holder's progress drains this many times faster. */
  hillDecay: 2,
  // --- AI (ai/nation/wonders.ts) -----------------------------------------------------------
  /**
   * Economic victory: a nation leading in gold saves once it has this share of the target.
   * Stage N: 0.4 -> 0.04 — leaders peaked at 1-5 M gold (Simple 1-1.4 M), never reached
   * 0.4 × 25 M, so the Economic mode played exactly like Conquest (identical games); 0.1 still
   * left Simple untouched.
   */
  aiEconomicSaveShare: 0.04,
  /** ... or always once a rival is past this share (it must not fall behind). */
  aiEconomicRaceShare: 0.6,
};

// =============================================================================
// 19i. ATTACK MODES → ARMY MIX (wave 4 stage E (7), module attacktypes; stage I, module army:
//      src/core/combat/armyMix.ts, armySystem.ts, docs/GAME.md#army-mix-and-recruitment-presets). Terrain terms per unit:
//      src/core/combat/terrainEffects.ts (module warfare).
// =============================================================================

/**
 * "The game picks the units: your recruitment preset sets the mix of infantry, artillery and
 * armor." (settings.attackTypes) One troop pool; the preset is a player-level ratio. Every land
 * attack takes the machines that suit the front it fights on (front sample), as far as the
 * completed factories and the metal + fuel stock allow, and keeps that composition. On each tile
 * a machine only fights where it helps (cost or speed better than infantry), otherwise its troops
 * fight as infantry. Cost terms join the clamped situational product (TERRAIN caps, design
 * rule 7); speed terms multiply the front speed weight (> 1 = slower).
 */
export const ATTACKMODES = {
  /** Kill switch (A/B runs); the game setting `attackTypes` turns the army mix off per game. */
  enabled: true,
  /**
   * Recruitment presets (doctrines): weights [infantry, artillery, armor] (normalised to shares),
   * the defence term (land of the player costs attackers × (1 + defense)) and the attack term
   * (the player's own attacks cost × (1 + attack), enemy and unowned land).
   */
  presets: {
    defensive: { weights: [70, 30, 0] as const, defense: 0.25, attack: 0.1 },
    balanced: { weights: [50, 25, 25] as const, defense: 0, attack: 0 },
    offensive: { weights: [25, 20, 55] as const, defense: -0.15, attack: -0.1 },
  },
  /**
   * Custom preset: defence = clamp((infantry share − 0.5) × customDefensePerInfantry), attack =
   * clamp((infantry share − 0.5) × customAttackPerInfantry) (an infantry army digs in, a machine
   * army pushes).
   */
  customDefensePerInfantry: 1,
  defenseMin: -0.2,
  defenseMax: 0.25,
  customAttackPerInfantry: 0.4,
  attackMin: -0.1,
  attackMax: 0.1,
  /** Default custom weights (before the player moved a slider). */
  customDefault: [50, 25, 25] as const,
  /** Machine share of an attack each completed factory level allows (0.3 → 4 levels for 100 %). */
  machinesPerFactory: 0.3,
  /** Front contacts sampled when an attack starts (composition). */
  frontSamples: 48,
  /** A machine gets its full share when it suits at least this fraction of the sampled front (less → proportional). */
  fullUseShare: 0.5,
  /** One attack may use at most this share of the metal + fuel (or gold) stock for its machines. */
  stockShare: 0.5,
  /**
   * A changed preset's doctrine terms (defence of your land, cost of your attacks) apply only after
   * this many seconds; the old ones hold until then (no instant flip to Defensive when attacked).
   */
  doctrineSettleSeconds: 10,
  /**
   * Gold per machine troop when the stockpile economy is off (with it on, metal + fuel per 1 000
   * machine troops come from ECONOMY2.artillery / ECONOMY2.armor via machineStockCost).
   */
  goldPerMachine: 1,
  /** AttackSummary.mode / front markers: the leading machine makes up at least this share of the attack. */
  leadShare: 0.15,
  /** "Artillery breaks defences": extra term against fortifications (on top of the terrain table). */
  artillery: {
    /** Troop cost on enemy tiles inside a defense post's range … */
    defendedCost: 0.55,
    /** … and on enemy chokepoints: × (1 − chokepointBonus × strength / 255). */
    chokepointBonus: 0.4,
    /** Guns set up before they fire: front speed weight against fortifications. */
    fortSpeed: 1.3,
  },
  /** Clamps of the blended terms (before the situational clamp). */
  minCost: 0.4,
  minSpeed: 0.45,
  maxSpeed: 2.5,
  // --- AI (ai/nation/army.ts) ----------------------------------------------------------------
  /** Decisions between two preset reviews (a threat is checked every decision). */
  aiReviewEvery: 3,
  /**
   * Incoming attack troops / own troops above which a nation turns defensive (rebalance after
   * stage I, review fix: 0.35 → 0.6; at 0.35 nearly every nation at war on hard / impossible sat
   * in Defensive, 61-65 % of the nation-minutes, and turned it on and off with every wave).
   */
  aiThreatShare: 0.6,
  /** Own troops / strongest rival neighbour's troops above which an aggressive nation turns offensive. */
  aiStrongShare: 1.2,
};

// ---------------------------------------------------------------------------
// 19p. PASSAGE RIGHTS (wave 4 P16, module passage; src/core/executions/diplomacy/passage*.ts,
//      docs/GAME.md#passage-rights)
// ---------------------------------------------------------------------------

/** "With an ally's permission your army may march through their land." (settings.passage) */
export const PASSAGE = {
  /** Kill switch; the game setting `passage` turns passage rights off per game. */
  enabled: true,
  /** An unanswered passage request expires after this many ticks (25 s). */
  requestTimeoutTicks: 250,
  /** After a decline the same player may ask again after this many ticks (45 s). */
  requestCooldownTicks: 450,
  /** After a revoke the same player may ask again after this many ticks (90 s). */
  revokeCooldownTicks: 900,
  // --- route search (passageRoute.ts) ---------------------------------------------------------
  /** Most allied tiles one route search visits (bounded BFS over the attacker's and the ally's land). */
  maxSearchTiles: 60_000,
  /**
   * Tiles the AI's route searches may visit per tick, game-wide (player intents are never budgeted).
   * Keep it ≥ maxSearchTiles so one full search fits in a tick (≈ 13 ms worst case on a loaded machine).
   */
  aiSearchBudgetPerTick: 60_000,
  /** With a clicked tile the arrival point minimises route length + hintWeight × distance to the click. */
  hintWeight: 1,
  // --- march ------------------------------------------------------------------------------------
  /** March time: marchBaseTicks + marchTicksPerTile × route length (tiles of allied land) … */
  marchBaseTicks: 10,
  marchTicksPerTile: 0.4,
  /** … at most this many ticks (30 s). */
  marchMaxTicks: 300,
  /** Most marches of one player at once. */
  maxMarches: 4,
  /** Route waypoints sent to the clients per march (the path is subsampled to this many tiles). */
  maxWaypoints: 40,
  // --- front ------------------------------------------------------------------------------------
  /** The front starts from the ally's tiles next to the target within this many tiles of the arrival point. */
  frontRadius: 10,
  /** Most front start tiles of one arrival. */
  maxStagingTiles: 96,
  /** Grantees with supplied (or cut-off) land are re-scanned by the region system this often (5 s). */
  supplyRecheckTicks: 50,
  // --- AI (ai/nation/passage.ts) ------------------------------------------------------------------
  /** Nations let an ally pass at this effective relation or more (teammates always pass). */
  aiGrantRelation: 20,
  /** … or at this relation when both fight the same enemy right now. */
  aiGrantRelationSharedEnemy: 5,
  /** Nations take passage back when the relation falls below this. */
  aiRevokeRelation: -10,
  /** A nation looks for targets it can only reach through allied land every this many decisions. */
  aiPlanEveryDecisions: 3,
  /** Smallest marching force / target troops for an AI passage attack. */
  aiMinStrength: 0.6,
  /** Share of the home troops an AI passage attack sends (× the personality's attack ratio / 0.3). */
  aiAttackShare: 0.35,
  /** Grudge above which a nation wants to reach a player through allied land. */
  aiGrudgeTarget: 25,
};

// ---------------------------------------------------------------------------
// 19q. DIPLOMACY PLUS (wave 4 P13, module diplomacy2; src/core/executions/diplomacy/pacts.ts,
//      tribute.ts, vassals.ts, diplomacy2*.ts; docs/GAME.md#diplomacy-plus-pacts-tribute-and-vassals)
// ---------------------------------------------------------------------------

/** "More options than war or alliance." (settings.diplomacyPlus) */
export const DIPLOMACY2 = {
  /** Kill switch; the game setting `diplomacyPlus` turns the module off per game. */
  enabled: true,
  // --- non-aggression pacts (pacts.ts) -------------------------------------------------------
  /** An unanswered pact offer expires after this many ticks (25 s). */
  pactOfferTimeoutTicks: 250,
  /** After a decline or a break the same player may offer again after this many ticks (60 s). */
  pactOfferCooldownTicks: 600,
  /** A pact lasts this long (4 min) … */
  pactDurationTicks: 2400,
  /** … and both sides may renew it (from its end, by another pactDurationTicks) in its last 45 s. */
  pactRenewWindowTicks: 450,
  /** Breaking a pact marks you as a traitor for this long (30 s; an alliance: DIPLOMACY.traitorDurationTicks = 60 s). */
  pactTraitorTicks: 300,
  /** Relation changes (nations only): both sides when a pact is made or renewed. */
  relationPactFormed: 10,
  /** The victim towards the player that broke the pact (an alliance: −80). */
  relationPactBrokenVictim: -40,
  /** Every other nation towards a pact breaker (an alliance: −20). */
  relationPactBrokenWitness: -8,
  /** The offering nation towards the player that declined its offer. */
  relationPactDeclined: -4,
  // --- tribute (tribute.ts) ------------------------------------------------------------------
  /** The demanded player has this long to pay (30 s). */
  tributeDeadlineTicks: 300,
  /** Smallest tribute (gold). */
  tributeMinGold: 25_000,
  /** Largest tribute: this share of the demanded player's gold when the demand is made. */
  tributeMaxShare: 0.5,
  /** The same player may demand tribute from the same player again only after this long (3 min). */
  tributeCooldownTicks: 1800,
  /** Most open demands of one demander at a time. */
  tributeMaxOpen: 2,
  /** Most open demands on one player at a time (spam guard). */
  tributeMaxIncoming: 3,
  /**
   * A refusal (or the deadline) gives the demander a just cause for this long (90 s): it may break
   * a pact or an alliance with the refuser without a traitor mark.
   */
  tributeCauseTicks: 900,
  /** Paying buys a truce: the demander may not attack the payer for this long (2 min). */
  tributeTruceTicks: 1200,
  /** Relation of a paying nation towards the demander (resentment). */
  relationTributePaid: -6,
  /** Relation of a demanding nation towards the payer (pleased). */
  relationTributeReceived: 8,
  /** Relation of a demanding nation towards a refuser. */
  relationTributeRefused: -12,
  /** Relation of a demanded nation towards the demander. */
  relationTributeDemanded: -6,
  // --- vassals (vassals.ts) ------------------------------------------------------------------
  /** A vassal has at most this share of its overlord's land … */
  vassalMaxLandRatio: 0.4,
  /** … and of its overlord's troops when the bond is made ("much weaker"). */
  vassalMaxTroopRatio: 0.5,
  /** Share of the vassal's worker income paid to its overlord. */
  vassalIncomeShare: 0.2,
  /** Most vassals of one overlord. */
  maxVassals: 3,
  /** An unanswered vassalage offer / request for protection expires after this many ticks (25 s). */
  vassalOfferTimeoutTicks: 250,
  /** After a decline, a release or a rebellion the same pair may not talk vassalage for this long (2 min). */
  vassalOfferCooldownTicks: 1200,
  /** A vassal may rebel at any time after this long (2 min) … */
  vassalMinTicks: 1200,
  /** … or earlier once its overlord weakened: the vassal has this share of its land or of its troops. */
  rebelStrengthRatio: 0.7,
  /** The bond dissolves on its own once the vassal has at least this share of the overlord's land and troops. */
  vassalFreeRatio: 1,
  /** A rebel's attacks on its former overlord cost this much less (× 0.8, within the attack clamp) … */
  rebelAttackBonus: 0.2,
  /** … for this long (60 s). */
  rebelBonusTicks: 600,
  /** Relation of a nation overlord towards its rebelling vassal. */
  relationRebellion: -35,
  /** Both sides when a bond is made. */
  relationVassalFormed: 15,
  /** The system checks the strength rule (bond dissolves) this often. */
  checkEveryTicks: 10,
  // --- AI (ai/nation/diplomacy2.ts) ----------------------------------------------------------
  /** Nations think about offers, demands and rebellions every this many decisions. */
  aiEveryDecisions: 3,
  /** Nations offer a pact to a land neighbour with at least this share of their troops that they do not want to fight now. */
  aiPactPeerRatio: 0.7,
  /** Nations demand tribute from land neighbours with at most this share of their troops … */
  aiTributeWeakRatio: 0.45,
  /** … and at least this much gold; they ask for this share of it. */
  aiTributeMinGold: 80_000,
  aiTributeShare: 0.3,
  /** Nations pay when the demander has at least this many times their troops. */
  aiPayStrengthRatio: 1.3,
  /** Doomed: a nation asks for protection when incoming attacks carry this many times its troops … */
  aiDoomedIncomingRatio: 1.5,
  /** … or a land neighbour attacking it has this many times its troops. */
  aiDoomedNeighbourRatio: 3,
  /** A vassal nation rebels (when it may) once it has this share of its overlord's troops … */
  aiRebelTroopRatio: 0.85,
  /** … or when its relation towards the overlord falls below this. */
  aiRebelRelation: -40,
  /** Share of the home troops a vassal nation sends into its overlord's war. */
  aiVassalWarShare: 0.2,
};

// =============================================================================
// 19r. RISING SEA (wave 4 P14, module risingsea; src/core/world/risingSea.ts,
//      src/core/world/risingSeaMap.ts, docs/GAME.md#rising-sea). Times in seconds at game
//      speed 1 (a faster game speed shortens them, like the seasons); the warning is real seconds.
// =============================================================================

/** "The ocean rises — the coasts are disappearing." (settings.risingSea: off / slow / normal / fast) */
export const RISINGSEA = {
  /** Kill switch; the game setting `risingSea` ("off" or absent) turns the mode off per game. */
  enabled: true,
  /** First rise after the spawn phase per pace. */
  firstRiseSeconds: { slow: 180, normal: 120, fast: 75 },
  /** Time between two rises per pace. */
  intervalSeconds: { slow: 150, normal: 100, fast: 60 },
  /** Every rise is announced this long before it floods (HUD countdown + the tiles about to flood highlighted). */
  warnSeconds: 20,
  /** The sea never rises above this level (elevation steps), whatever the map. */
  maxLevel: 12,
  /** Per-map cap: the final level keeps at least this share of the land dry (a playable core remains). */
  minLandShare: 0.5,
  /**
   * A rise that floods more tiles than this is spread over several ticks (spatial row bands, one
   * band every sliceEveryTicks ticks), so no single tick (and no single client repaint) takes the
   * whole coastline at once.
   */
  sliceTiles: 3_000,
  sliceEveryTicks: 1,
  /**
   * After the last band the derived map data catches up over the next ticks, a bounded amount per
   * tick (no tick above the 40 ms budget): the chokepoint / strait patch in windows of at most this
   * many tiles per tick (GameMap.patchDeferredTactical; ≈ 50–75 ns per window tile) …
   */
  settleTacticalTiles: 110_000,
  /** … and this many steps of the ships' NavGrid rebuild per tick (WaterPathfinder.ts NavRebuild; ≈ 3–8 ms each). */
  settleNavSteps: 1,
  /**
   * The bands and the catching up skip the ticks on which the periodic whole-game passes run (the
   * cut-off region scan, the enclave check, the state hash: every REGIONS.scanInterval /
   * ENCLAVE.checkInterval / HASH_INTERVAL ticks), so they never add up in one tick; a rise due on
   * such a tick starts one tick later.
   */
  skipBusyTicks: true,
  /** Home troops lost: this share × troops × (own tiles flooded / own tiles before the flood). */
  troopLossShare: 1,
  /** Moving the capital to higher ground (intent `moveCapital`, only while it stands on land that will flood). */
  moveCapitalGold: 100_000,
  /** Setup screen: suggested settings for short, chaotic games (applied with one click). */
  suggestedTimeLimitMinutes: { slow: 45, normal: 30, fast: 20 },
  suggestedWinPercent: 70,
  // --- AI (src/core/ai/nation/risingSea.ts) ----------------------------------------------------
  /** Nations move their capital once it would flood within this many rises (and they can pay). */
  aiCapitalRises: 1,
  /** Nations do not build on land that floods within this many rises (ports: the only rule for them) … */
  aiAvoidRises: 2,
  /** … and other structures avoid every tile that floods before the final level when a dry site exists. */
  aiAvoidDoomed: true,
  /** A nation replaces a completed city / factory that floods at the next rise with one on dry land. */
  aiReplaceStructures: true,
  /** Land targets: score bonus per share of dry land (never floods) behind the front, minus the same for doomed land. */
  aiHighGroundScore: 0.6,
  /** … and the attack margin grows by this share of doomed land at the front. */
  aiDoomedMargin: 0.3,
  /** Expansions: an aim whose surroundings are at least this share doomed is mirrored toward dry land. */
  aiExpansionDoomedShare: 0.4,
  /** Radius (tiles) sampled around an expansion aim. */
  aiSampleRadius: 10,
};

// =============================================================================
// 19s. ECONOMY2 — stockpiles (wave 4 stage I, module economy2; src/core/economy/stockpile.ts,
//      src/core/economy/stockCosts.ts, docs/GAME.md#resources-extractors-and-stockpiles). Amounts are stock units; rates
//      are per second at game speed 1. Food: 1 unit feeds `troopsPerFood` troops for one second.
// =============================================================================

/** A stock amount per kind (types.ts StockKind), e.g. { wood: 80, stone: 60 }. */
export type StockAmounts = Partial<Record<"food" | "wood" | "stone" | "metal" | "fuel", number>>;

/**
 * "Your land fills five stockpiles; building, war machines and troops use them up."
 * (settings.stockpiles, needs settings.resources). Index order of every 5-array: food, wood,
 * stone, metal, fuel (types.ts STOCK_KINDS). Humans and nations only (tribes keep the old rules).
 */
export const ECONOMY2 = {
  /** Kill switch (A/B runs); the game settings `stockpiles` + `resources` turn it on per game. */
  enabled: true,
  /** Production, upkeep and storage are settled this often (ticks). */
  updateTicks: 10,
  /** Stock every player starts with when it spawns (two cities, an extractor and some war stock). */
  start: [500, 400, 300, 200, 150],
  /**
   * Deposits yield with diminishing returns like the population: production of a stock =
   * rate × n^0.75, n = own deposit tiles of the type (extractor-boosted tiles count 1 + boost,
   * a gold rush adds its extra tiles). `deposit[r]` = rates per ResourceType (0 none, grain, wood,
   * stone, metal, oil, animals) → 5-array of stocks. Metal mines also dig a little coal (fuel).
   */
  deposit: [
    [0, 0, 0, 0, 0],
    [1.2, 0, 0, 0, 0],
    [0, 0.6, 0, 0, 0],
    [0, 0, 0.6, 0, 0],
    [0, 0, 0, 0.5, 0.1],
    [0, 0, 0, 0, 0.5],
    [0.8, 0, 0, 0, 0],
  ],
  /**
   * Grain and animal deposits yield × this per climate (temperate, desert, tundra, tropical,
   * steppe), climate feature on; the home climate's penalty is waived (like the max population).
   */
  climateFood: [1, 0.55, 0.5, 1.1, 0.9],
  /**
   * Plain land: it feeds landFeed × (baseMax + perTileK × tiles^0.75 × population multiplier)
   * troops (the land part of the max population with the same climate / home-climate / grain
   * multiplier), so at the default troop share an empire without food deposits just gets by in
   * every climate; forests (temperate / tropical land), highlands and
   * mountains add a little wood, stone and scrap metal: rate × tiles^0.75 per second.
   */
  landFeed: 0.6,
  /**
   * Every completed city level (and capital level) feeds this many troops (markets and farms):
   * its population share at the default troop ratio (0.6 × POPULATION.cityPerLevel).
   */
  cityFeed: 30_000,
  landWood: 0.0075,
  landStone: 0.015,
  landMetal: 0.005,
  /**
   * Fish: rate × harvested fish tiles^0.75 food per second; the share of them within
   * portRadius of an own completed port yields × (1 + portBoost).
   */
  fishFood: 1,
  portBoost: 0.6,
  portRadius: 14,
  /** Fish within this many tiles (Chebyshev) of a coast are harvested by the owner of that coast tile. */
  fishReach: 2,
  /** Production per season (spring, summer, autumn, winter) for each stock (seasons on). */
  season: [
    [1, 1, 1, 1, 1],
    [1.1, 1, 1, 1, 1],
    [1.3, 1, 1, 1, 1],
    [0.75, 0.85, 0.9, 1, 1],
  ],
  /** Fish do not follow the farming seasons (only this winter factor). */
  fishWinter: 0.9,
  /** Troops one unit of food feeds for one second (upkeep = home troops / troopsPerFood per second). */
  troopsPerFood: 1_000,
  /**
   * Food deficit: with food in store, growth × max(rationGrowth, fed); with an empty store,
   * growth × clamp((fed − starveGrowthFloor) / (1 − starveGrowthFloor), 0, 1); below `starveBelow`
   * fed (empty store) the troops nobody feeds desert: starveShare of them per second.
   */
  rationGrowth: 0.5,
  starveGrowthFloor: 0.5,
  starveBelow: 0.9,
  starveShare: 0.04,
  /**
   * Storage cap per stock: base + perTile × tiles^0.75 + perCity × city (and capital) levels
   * (food: about a minute of upkeep at the default troop share).
   */
  capBase: [800, 800, 800, 800, 800],
  capPerTile: [4, 3, 3, 2.5, 2.5],
  capPerCity: [1500, 250, 250, 200, 200],
  /**
   * Costs (stock units) by unit type: structures and their upgrades (wood + stone), ships and
   * missiles (metal + fuel). Structures cost × min(maxEscalation, 1 + escalation × own levels of
   * the type), like their gold price escalates.
   */
  costs: {
    city: { wood: 150, stone: 100 },
    port: { wood: 150, stone: 50 },
    defense_post: { wood: 20, stone: 80 },
    missile_silo: { stone: 200, metal: 150 },
    sam_launcher: { stone: 100, metal: 120 },
    factory: { wood: 150, stone: 150, metal: 30 },
    extractor: { wood: 60, stone: 40 },
    shipyard: { wood: 250, stone: 100, metal: 80 },
    airfield: { stone: 150, metal: 100, fuel: 60 },
    warship: { wood: 40, metal: 100, fuel: 50 },
    patrol_boat: { wood: 20, metal: 30, fuel: 20 },
    battleship: { metal: 400, fuel: 200 },
    atom_bomb: { metal: 150, fuel: 120 },
    hydrogen_bomb: { metal: 400, fuel: 300 },
    mirv: { metal: 800, fuel: 600 },
  } as Record<string, StockAmounts>,
  escalation: 0.15,
  maxEscalation: 4,
  /** Air force uses (executions/air): one air strike sortie, one special-forces drop. */
  airStrike: { metal: 25, fuel: 50 } as StockAmounts,
  airDrop: { fuel: 30 } as StockAmounts,
  /**
   * War machines (module army): stock per 1 000 troops of artillery / armor recruited. Stage M
   * tried metal 3 / 4 with metal deposits × 0.8: Full games stalled (docs/BALANCE.md#economy-findings).
   */
  artillery: { metal: 2, fuel: 1 } as StockAmounts,
  armor: { metal: 3, fuel: 3 } as StockAmounts,
  /**
   * Other weapons (module weapons) by key: weaponStockCost(game, key) reads this table (module
   * weapons owns these entries and may add keys). Unknown keys cost nothing.
   */
  weapons: {
    napalm: { metal: 50, fuel: 100 },
    clusterBomb: { metal: 100, fuel: 50 },
    bunkerBuster: { metal: 150, fuel: 60 },
    defoliant: { metal: 20, fuel: 80 },
    seaMine: { metal: 40, fuel: 10 },
  } as Record<string, StockAmounts>,
  /**
   * Ally trade (every tradeTicks): a player short of a stock (below shortShare of its cap) gets it
   * from the friend (ally / teammate, no embargo either way, sharing a border or both owning a
   * completed port) with the largest surplus above surplusShare of its cap: at most tradeShare of
   * the giver's cap per round; tradeLoss of every shipment is lost on the way.
   */
  tradeTicks: 50,
  shortShare: 0.25,
  surplusShare: 0.5,
  tradeShare: 0.15,
  tradeLoss: 0.15,
  // --- nation AI (src/core/ai/nation/economy2.ts) ---------------------------------------------
  /** Expansion: deposit weight × (1 + aiNeedWeight × need) for a stock the nation is short of. */
  aiNeedWeight: 2,
  /** Alliance score per unit of "the partner has a surplus of what we lack" (0..1). */
  aiAllianceNeed: 25,
  /** A starving nation lowers its troop ratio by this much per decision (down to aiMinRatio). */
  aiRatioStep: 0.05,
  aiMinRatio: 0.3,
  /**
   * An urgent build short of stock is saved up for only when the stock fits in the storage and
   * its net production (production + ally trade − use) gathers it within this many ticks.
   */
  aiSaveTicks: 3_000,
};

// =============================================================================
// 19t. WEAPONS (wave 4 stage I, module weapons; src/core/executions/weapons/**, nuke hooks in
//      src/core/executions/nukes/**, docs/GAME.md#modern-weapons). Feature toggle settings.weapons.
// =============================================================================

/**
 * "Weapons with consequences: they burn, poison and sink the economy of your enemy."
 * Bombs (napalm, cluster, bunker buster, defoliant) fly from the nearest own completed missile
 * silo or airfield within `range` (not knocked out by an EMP); sea mines are laid from the nearest
 * own port within seaMines.layRange. Every use costs gold, plus metal + fuel with the stockpile
 * economy (ECONOMY2.weapons), and starts that weapon's own cooldown for the player.
 */
export const WEAPONS = {
  /** Kill switch (A/B runs); the game setting `weapons` turns the module off per game. */
  enabled: true,
  /** Reach (tiles) from the silo / airfield to the target. */
  range: 220,
  /** Flight: tiles per tick, clamped to [minFlightTicks, maxFlightTicks]. */
  flightSpeed: 3,
  minFlightTicks: 10,
  maxFlightTicks: 90,
  /** Relation change of a hit nation toward the launcher. */
  relationPenalty: -12,
  /**
   * "Napalm sets forest and grassland on fire: the flames spread through the woods until rain or
   * water stops them, kill troops and animals, and the land takes long to regrow."
   */
  napalm: {
    gold: 180_000,
    cooldownSeconds: 30,
    /** Disc ignited at once (tiles). */
    radius: 3,
    /** Share of the troops standing on the hit land that die (at most maxKillShare of a victim's home troops). */
    killShare: 0.35,
    maxKillShare: 0.05,
    /** Attacks whose front runs through the fire lose this share × (front tiles hit / all front tiles). */
    attackKillShare: 0.25,
    /** A tile burns this long (rain halves it). */
    burnSeconds: 8,
    /** The fire spreads for this long after the impact, one step every spreadEveryTicks ticks … */
    spreadSeconds: 45,
    spreadEveryTicks: 5,
    /**
     * … to each 4-neighbouring tile that can burn, with this chance per step: forest (wood
     * deposits), fields and pastures (grain / animal deposits), and other grass- and woodland
     * (temperate, tropical and steppe land below the mountains; highlands × highlandFactor) …
     */
    spreadChance: 0.85,
    fieldSpreadChance: 0.55,
    grassSpreadChance: 0.3,
    highlandFactor: 0.6,
    /** … and burns at most this many tiles in total (incl. the first disc). */
    maxTiles: 260,
    /** Burnt land: its grain / wood / animal deposit comes back after this long. */
    regrowSeconds: 600,
  },
  /** "A cluster bomb shreds the troops in a wide area but leaves land and buildings untouched." */
  cluster: {
    gold: 150_000,
    cooldownSeconds: 20,
    radius: 6,
    killShare: 0.55,
    maxKillShare: 0.14,
    /** Border tiles hold this many times the troops of an interior tile. */
    frontWeight: 4,
    /** Attacks whose front runs through the area lose this share × (front tiles hit / all front tiles). */
    attackKillShare: 0.6,
  },
  /** "A bunker buster destroys the one strongest structure it hits (forts first) and nothing else." */
  bunkerBuster: {
    gold: 220_000,
    cooldownSeconds: 25,
    /** The structure is searched within this radius (tiles) of the target. */
    radius: 3,
  },
  /** "Defoliant kills crops and forests in a wide area for a long time; nobody gets hurt." */
  defoliant: {
    gold: 160_000,
    cooldownSeconds: 45,
    radius: 7,
    /** Grain and wood deposits of hostile or unowned land come back after this long. */
    regrowSeconds: 900,
  },
  /**
   * "Sea mines hidden in the water sink or damage enemy ships that sail through; only you and your
   * allies can see them."
   */
  seaMines: {
    gold: 100_000,
    cooldownSeconds: 20,
    /** The field must lie within this many tiles of an own completed port. */
    layRange: 30,
    /** Field radius (tiles) and hits it can deal before it is spent. */
    radius: 3,
    charges: 3,
    /** A field clears itself after this long. */
    lifetimeSeconds: 600,
    /** Fields one player may have at once. */
    maxFields: 6,
    /** Damage to a warship-like ship per hit (share of its max health); other ships sink. */
    shipDamage: 0.6,
    /** Fields check for ships every this many ticks. */
    checkEveryTicks: 2,
  },
  /** Nuke consequences (with settings.nukeEffects; MIRV warheads by silos with settings.weapons). */
  nukes: {
    /** Grain, wood, animal (and fish) deposits in a blast come back only after this long (0 = never). */
    depositRegrowSeconds: 900,
    /** Crater flooding: crater land at or below this elevation floods when it touches the sea … */
    floodMaxElevation: 3,
    /** … one ring every floodEveryTicks ticks, at most floodTilesPerStep tiles per ring. */
    floodEveryTicks: 4,
    floodTilesPerStep: 160,
    /** MIRV warheads = min(mirvMaxWarheads, mirvBaseWarheads + mirvWarheadsPerSilo × (silo levels − 1)). */
    mirvBaseWarheads: 8,
    mirvWarheadsPerSilo: 6,
    mirvMaxWarheads: 80,
    /** Spread radius = min(NUKES.mirvSpreadRadius, mirvSpreadBase + mirvSpreadPerWarhead × warheads). */
    mirvSpreadBase: 30,
    mirvSpreadPerWarhead: 2,
  },
  // --- AI (src/core/ai/nation/weapons.ts) ------------------------------------------------------
  /** Decisions between two weapon decisions of one nation (medium: × 2, easy: × 3). */
  aiEvery: 3,
  /** A nation fires at most one weapon per this many seconds (× the same difficulty factor). */
  aiMinGapSeconds: 25,
  /** Gold a nation keeps after paying for a weapon. */
  aiReserveGold: 250_000,
  /** Cluster bomb: expected kills per 100 000 gold. */
  aiMinKillsPer100k: 1_500,
  /** Napalm / defoliant: hostile living deposit tiles the area must hold at least. */
  aiMinDeposits: 5,
  /** Napalm: the target stays this far (tiles) from own land (the fire spreads through any forest). */
  aiNapalmClearance: 12,
  /** A nation plans a MIRV only when it would carry at least this many warheads (enough silos). */
  aiMirvMinWarheads: 20,
  /** Sea mines: a nation lays at most this many fields. */
  aiMaxFields: 2,
  /**
   * Sea mines: a nation lays a new field at most once per this many seconds (× the difficulty's
   * cadence factor). Stage M: mines were ~80 % of the hard+ launches with only the weapon gap.
   */
  aiMineGapSeconds: 120,
  /** Candidate tiles scored per decision. */
  aiCandidates: 40,
};
